import React from "react";
import {
  FiArrowLeft,
  FiArrowLeftCircle,
  FiArrowRight,
  FiCalendar,
  FiCheck,
  FiCheckCircle,
  FiChevronDown,
  FiChevronLeft,
  FiChevronRight,
  FiClock,
  FiHelpCircle,
  FiHome,
  FiInfo,
  FiMoreHorizontal,
  FiUsers,
  FiX,
  FiXCircle,
} from "react-icons/fi";
import { colors } from "./colors";

export type IconName =
  | "calendar"
  | "check"
  | "check-circle"
  | "clock"
  | "info"
  | "city"
  | "filter"
  | "globe"
  | "bottom-bar-navigation-home"
  | "bottom-bar-navigation-reporting"
  | "bottom-bar-navigation-reservations"
  | "bottom-bar-navigation-support"
  | "bottom-bar-navigation-profile"
  | "bottom-bar-navigation-team-spaces"
  | "reporting-date-range-picker"
  | "filter-active"
  | "home"
  | "arrow-right"
  | "arrow-left"
  | "dots"
  | "arrow-left-circle"
  | "chevron-right"
  | "chevron-left"
  | "chevron-down"
  | "location-pin"
  | "map-pin"
  | "map-pin-filled"
  | "x-circle"
  | "x"
  | "booking-buffer"
  | "disinfection"
  | "distance"
  | "schedule"
  | "checkin"
  | "masks"
  | "sanitizer"
  | "promotion"
  | "temperature"
  | "users"
  | "help-circle"
  | "lock"
  | "success-check"
  | "map-location-icon"
  | "icon-close"
  | "pencil"
  | "list-view"
  | "feather-chevron-down"
  | "feather-chevron-up"
  | "map-view"
  | "tour-close"
  | "eye-open"
  | "eye-closed"
  | "location"
  | "location-filled"
  | "menu-icon"
  | "invite-member"
  | "plus-circle"
  | "map-view"
  | "copy-icon"
  | "launch-icon"
  | "magnifying-glass"
  | "mail"
  | "map-searched-icon"
  | "outgoing";

type IconSize = "sm" | "md" | "lg";

interface IconBaseProps {
  color?: string;
  size?: IconSize | number;
}

interface IconProps extends IconBaseProps {
  name: IconName;
}

export function Icon(props: IconProps) {
  const { name, color, size = "md" } = props;

  switch (name) {
    case "calendar":
      return (
        <FiCalendar
          strokeWidth={1}
          color={color}
          size={typeof size === "number" ? size : sizeMap[size]}
        />
      );
    case "check":
      return (
        <FiCheck
          color={color}
          size={typeof size === "number" ? size : sizeMap[size]}
        />
      );
    case "info":
      return (
        <FiInfo
          color={color}
          size={typeof size === "number" ? size : sizeMap[size]}
        />
      );
    case "schedule":
      return (
        <ScheduleIcon
          color={color}
          size={typeof size === "number" ? size : sizeMap[size]}
        />
      );
    case "globe":
      return <GlobeIcon />;
    case "checkin":
      return (
        <CheckinIcon
          color={color}
          size={typeof size === "number" ? size : sizeMap[size]}
        />
      );
    case "clock":
      return (
        <FiClock
          color={color}
          strokeWidth={1}
          size={typeof size === "number" ? size : sizeMap[size]}
        />
      );
    case "city":
      return <CityIcon />;
    case "reporting-date-range-picker":
      return <ReportingDateRangePickerIcon />;
    case "home":
      return (
        <FiHome
          color={color}
          size={typeof size === "number" ? size : sizeMap[size]}
        />
      );
    case "arrow-right":
      return (
        <FiArrowRight
          color={color}
          size={typeof size === "number" ? size : sizeMap[size]}
        />
      );
    case "arrow-left":
      return (
        <FiArrowLeft
          color={color}
          size={typeof size === "number" ? size : sizeMap[size]}
        />
      );
    case "arrow-left-circle":
      return (
        <FiArrowLeftCircle
          strokeWidth={1}
          color={color}
          size={typeof size === "number" ? size : sizeMap[size]}
        />
      );
    case "dots":
      return (
        <FiMoreHorizontal
          color={color}
          size={typeof size === "number" ? size : sizeMap[size]}
        />
      );
    case "chevron-right":
      return (
        <FiChevronRight
          color={color}
          size={typeof size === "number" ? size : sizeMap[size]}
        />
      );
    case "chevron-left":
      return (
        <FiChevronLeft
          color={color}
          size={typeof size === "number" ? size : sizeMap[size]}
        />
      );
    case "chevron-down":
      return (
        <FiChevronDown
          color={color}
          size={typeof size === "number" ? size : sizeMap[size]}
        />
      );
    case "location-pin":
      return <LocationPinIcon />;
    case "map-pin":
      return <MapPinIcon fill={color} />;
    case "map-pin-filled":
      return <MapPinIconFilled fill={color} />;
    case "users":
      return (
        <FiUsers
          strokeWidth={1}
          color={color}
          size={typeof size === "number" ? size : sizeMap[size]}
        />
      );
    case "x-circle":
      return (
        <FiXCircle
          strokeWidth={1}
          color={color}
          size={typeof size === "number" ? size : sizeMap[size]}
        />
      );
    case "x":
      return (
        <FiX
          color={color}
          size={typeof size === "number" ? size : sizeMap[size]}
        />
      );
    case "help-circle":
      return (
        <FiHelpCircle
          color={color}
          size={typeof size === "number" ? size : sizeMap[size]}
        />
      );
    case "check-circle":
      return (
        <FiCheckCircle
          color={color}
          size={typeof size === "number" ? size : sizeMap[size]}
        />
      );
    case "lock":
      return (
        <LockedIcon size={typeof size === "number" ? size : sizeMap[size]} />
      );
    case "success-check":
      return <SuccessCheckIcon />;
    case "booking-buffer":
      return <BufferTimeIcon />;
    case "disinfection":
      return <CleaningIcon />;
    case "distance":
      return <DistanceIcon />;
    case "filter":
      return <FilterIcon />;
    case "filter-active":
      return <FilterActiveIcon />;
    case "masks":
      return <MaskIcon />;
    case "promotion":
      return <PromotionIcon />;
    case "sanitizer":
      return <SanitizationIcon />;
    case "temperature":
      return <TemperatureIcon />;
    case "bottom-bar-navigation-home":
      return <BottomBarNavigationHomeIcon />;
    case "bottom-bar-navigation-reservations":
      return <BottomBarNavigationReservationsIcon />;
    case "bottom-bar-navigation-support":
      return <BottomBarNavigationSupportIcon />;
    case "bottom-bar-navigation-profile":
      return <BottomBarNavigationProfileIcon />;
    case "bottom-bar-navigation-reporting":
      return <BottomBarNavigationReportingIcon />;
    case "bottom-bar-navigation-team-spaces":
      return <ButtomNavigationTeamSpacesIcon />;
    case "pencil":
      return <PencilIcon color={color} />;
    case "map-location-icon":
      return <MapLocationIcon />;
    case "icon-close":
      return <IconClose />;
    case "list-view":
      return <ListViewIcon />;
    case "map-view":
      return <MapViewIcon />;
    case "feather-chevron-down":
      return <FeatherChevronDown />;
    case "feather-chevron-up":
      return <FeatherChevronUp />;
    case "tour-close":
      return <TourClose />;
    case "eye-open":
      return <EyeOpen />;
    case "eye-closed":
      return <EyeClosed />;
    case "location":
      return <IconLocation />;
    case "location-filled":
      return <IconLocationFilled />;
    case "menu-icon":
      return <MenuIcon color={color} />;
    case "invite-member":
      return <InviteMemberIcon />;
    case "plus-circle":
      return <IconPlusCircle />;
    case "copy-icon":
      return <CopyIcon />;
    case "launch-icon":
      return <LaunchIcon color={color} />;
    case "magnifying-glass":
      return <MagnifyingGlass />;
    case "mail":
      return <MailIcon />;
    case "outgoing":
      return <OutgoingIcon fill={color} />;
    case "map-searched-icon":
      return <MapSearchedIcon />;
    default:
      throw new Error(`Missing icon ${name}`);
  }
}

const sizeMap: { [size in IconSize]: number } = {
  lg: 32,
  md: 24,
  sm: 16,
};

function BufferTimeIcon() {
  return (
    <svg width={23} height={24} viewBox="0 0 23 24" fill="none">
      <path
        d="M11.5 23C17.299 23 22 18.075 22 12S17.299 1 11.5 1 1 5.925 1 12s4.701 11 10.5 11z"
        stroke="#000"
        strokeWidth={0.7}
      />
      <path
        d="M11.762 2.837v3.1M11.762 18.325v3.1M20.556 11.631h-2.1M5.069 11.631h-2.1"
        stroke="#000"
        strokeWidth={0.7}
        strokeLinecap="round"
      />
      <path stroke="#000" strokeWidth={0.7} d="M11.675 13.55L11.675 8.35005" />
      <path
        transform="matrix(-.55776 -.83 .69043 -.7234 15.475 17.784)"
        stroke="#000"
        strokeWidth={0.7}
        d="M0 -0.35L6.05029 -0.35"
      />
    </svg>
  );
}

function CleaningIcon() {
  return (
    <svg width={19} height={19} viewBox="0 0 19 19" fill="none">
      <path
        d="M18.762 6.056h-.7c-.422-1.178-1.18-1.56-1.547-1.677v-.653c0-1.013.4-1.462 1.384-1.552a.238.238 0 00.216-.236v-1.7A.238.238 0 0017.878 0h-4.87a.238.238 0 00-.237.237v.126h-.487a.963.963 0 000 1.925h.487v.004c0 .132.106.238.237.238h.89c-.187.468-.71 1.34-2.01 1.34a.238.238 0 000 .476c1.08 0 1.724-.516 2.097-1.024v1.107c-.31.195-1.12.77-1.45 1.627h-1.81V.918A.918.918 0 009.81 0h-.62a.918.918 0 00-.916.918v5.138h-.896V4.85c0-.868-.668-1.582-1.516-1.659V2.047c0-.3-.245-.545-.545-.545h-.06V.238A.238.238 0 005.02 0H3.314a.238.238 0 00-.238.237v1.265h-.059c-.3 0-.545.245-.545.545v1.144a1.669 1.669 0 00-1.516 1.66v1.205H.238A.238.238 0 000 6.294v2.222c0 .13.106.237.237.237h.208v8.238C.445 18.1 1.345 19 2.45 19h14.003a2.008 2.008 0 002.004-2.009V8.753h.306A.237.237 0 0019 8.516V6.294a.237.237 0 00-.238-.238zm-6.478-4.243a.488.488 0 010-.975h.487v.975h-.487zm.962-1.338h4.394v1.253c-1.076.167-1.6.823-1.6 1.998v.6h-1.58V2.292c0-.007-.004-.014-.005-.022a.23.23 0 00-.051-.125.234.234 0 00-.068-.057.228.228 0 00-.044-.019c-.007-.002-.013-.007-.02-.009-.01-.002-.017 0-.025 0-.009-.001-.016-.005-.025-.005h-.976V.475zm1.04 4.326h1.973c.11.02.854.194 1.296 1.255h-4.503c.34-.676 1.042-1.137 1.235-1.255zM9.5 9.738c.793 0 1.438.647 1.438 1.443 0 .795-.645 1.443-1.438 1.443a1.442 1.442 0 01-1.438-1.443c0-.796.645-1.443 1.438-1.443zM9.19.475h.62c.243 0 .44.199.44.443v8.499a1.897 1.897 0 00-1.5 0V.918c0-.244.197-.443.44-.443zm-5.639 0h1.232v1.027H3.55V.475zm-.534 1.502h2.3a.07.07 0 01.07.07v1.137h-2.44V2.047a.07.07 0 01.07-.07zM1.431 4.85c0-.657.535-1.191 1.192-1.191h3.088c.657 0 1.192.534 1.192 1.191v1.206H1.43V4.85zM.475 6.531h7.8v1.747h-7.8V6.531zm17.506 10.46c0 .846-.686 1.534-1.529 1.534H2.45A1.533 1.533 0 01.92 16.991V8.753h7.354v.957A1.918 1.918 0 009.5 13.099a1.918 1.918 0 001.226-3.39v-.956h7.255v8.238zm.544-8.713h-7.8V6.531h7.8v1.747z"
        fill="#000"
      />
    </svg>
  );
}

function DistanceIcon() {
  return (
    <svg width={37} height={8} viewBox="0 0 37 8" fill="none">
      <path
        d="M6.75 3.537c0 1.818-1.458 3.287-3.25 3.287S.25 5.355.25 3.537C.25 1.72 1.708.25 3.5.25s3.25 1.47 3.25 3.287zM36.178 3.537c0 1.818-1.457 3.287-3.25 3.287-1.792 0-3.25-1.469-3.25-3.287 0-1.818 1.458-3.287 3.25-3.287 1.793 0 3.25 1.47 3.25 3.287z"
        stroke="#000"
        strokeWidth={0.5}
      />
      <path
        d="M29.605 3.209a.25.25 0 000-.354l-1.59-1.59a.25.25 0 10-.354.353l1.414 1.414-1.414 1.414a.25.25 0 00.353.354l1.591-1.591zm-23.034.073H29.43v-.5H6.57v.5z"
        fill="#000"
      />
      <path
        d="M6.395 2.855a.25.25 0 000 .354l1.59 1.59a.25.25 0 00.354-.353L6.925 3.032l1.414-1.414a.25.25 0 00-.353-.354L6.395 2.855zm23.034-.073H6.57v.5H29.43v-.5z"
        fill="#000"
      />
    </svg>
  );
}

function MaskIcon() {
  return (
    <svg width={31} height={10} viewBox="0 0 31 10" fill="none">
      <path
        d="M26.383 1.128c-.16 0-.319.013-.474.033V.725c0-.4-.416-.725-.926-.725H5.248c-.51 0-.926.325-.926.725v.436a3.83 3.83 0 00-.475-.033C1.726 1.128 0 2.865 0 5s1.726 3.872 3.848 3.872c.161 0 .319-.013.475-.033v.436c0 .4.415.725.926.725h19.734c.51 0 .926-.325.926-.725v-.436c.155.02.313.033.474.033 2.122 0 3.849-1.737 3.849-3.872s-1.727-3.872-3.849-3.872zm-22.06 7.22c-.155.024-.313.04-.475.04C2.067 8.389.618 6.869.618 5c0-1.868 1.449-3.388 3.23-3.388.162 0 .32.016.475.04v6.696zm20.968.927c0 .133-.138.242-.308.242H5.248c-.17 0-.309-.109-.309-.242V.725c0-.133.139-.242.31-.242h19.733c.17 0 .308.109.308.242v8.55zm1.092-.886c-.161 0-.319-.017-.474-.041V1.652c.155-.024.313-.04.474-.04 1.782 0 3.231 1.52 3.231 3.388 0 1.868-1.45 3.389-3.23 3.389zm-4.678-5.255c0 .133-.138.242-.31.242H8.836c-.17 0-.308-.109-.308-.242s.138-.242.309-.242h12.56c.17 0 .309.109.309.242zm0 3.732c0 .134-.138.242-.31.242H8.836c-.17 0-.308-.108-.308-.242 0-.133.138-.242.309-.242h12.56c.17 0 .309.109.309.242z"
        fill="#000"
      />
    </svg>
  );
}

function SanitizationIcon() {
  return (
    <svg width={26} height={19} viewBox="0 0 26 19" fill="none">
      <path
        d="M25.691 7.766a.296.296 0 00-.281 0l-8.157 3.515H12.03a.145.145 0 00-.07 0c-.81.214-.81 1.14-.81 1.485v.213l-3.521-.872V6.466c0-.62-.497-1.122-1.11-1.122h-.65v-.89a.295.295 0 00-.294-.298h-.88V2.97h4.4c.487 0 .881-.399.881-.89a.885.885 0 00-.88-.892H4.695V.595h1.467a.295.295 0 00.293-.297A.295.295 0 006.162 0H1.467a.295.295 0 00-.293.297c0 .164.131.297.293.297h1.467v3.562h-.88a.295.295 0 00-.294.297v.89h-.65C.496 5.344 0 5.847 0 6.467v11.412C0 18.498.497 19 1.11 19h5.41c.294 0 .576-.118.784-.329.208-.21.325-.496.325-.793V14.7l7.963 2.375c.191.055.388.082.587.083.17.002.34-.018.505-.059l8.913-2.256a.29.29 0 00.223-.297V8.016c0-.1-.048-.193-.129-.25zM3.521.594h.587v.89c0 .164.131.297.293.297h4.695c.162 0 .293.133.293.297a.295.295 0 01-.293.297H4.4a.295.295 0 00-.293.297v1.484H3.52V.594zM2.347 4.75h2.934v.594H2.347V4.75zm4.695 9.773v3.355a.525.525 0 01-.522.528H1.11a.525.525 0 01-.523-.528V6.466c0-.292.234-.529.522-.529h5.41c.29 0 .523.237.523.529v5.51h-.176a.792.792 0 00-.17.036h-.047a.699.699 0 00-.182.077c-.067.038-.13.084-.188.136-.261.22-.412.547-.41.89v.203c.001.225.071.445.199.63.14.214.347.376.587.462l.387.113zm18.192-.208l-8.703 2.203a1.468 1.468 0 01-.769 0l-8.89-2.66h-.03a.523.523 0 01-.293-.231.527.527 0 01-.094-.297v-.202a.543.543 0 01.182-.41.67.67 0 01.188-.107h.035a.52.52 0 01.158 0l4.355 1.045h4.765a.295.295 0 00.293-.297.295.295 0 00-.293-.296h-4.401v-.297c0-.636.135-.826.334-.891h5.24a.28.28 0 00.118 0l7.805-3.408v5.848z"
        fill="#000"
      />
      <path
        d="M9.39 6.828c0 .492.393.89.88.89.486 0 .88-.398.88-.89 0 0-.147-2.078-.88-2.078-.734 0-.88 2.054-.88 2.078zm.88-1.348c.157.434.256.887.293 1.348a.295.295 0 01-.293.297.295.295 0 01-.294-.297c.037-.46.136-.914.293-1.348z"
        fill="#000"
      />
    </svg>
  );
}

function TemperatureIcon() {
  return (
    <svg width={19} height={19} viewBox="0 0 19 19" fill="none">
      <path
        d="M18.444 4.493c.863-.912.707-2.5-.364-3.57-.52-.521-1.18-.844-1.854-.91-.67-.066-1.274.129-1.715.546-.004.003-.01.005-.014.009L9.333 5.73h-.001v.001L7.843 7.22v.001l-3.675 3.673c-.006.006-.008.013-.013.02-.907.913-1.087 1.597-1.277 2.322-.252.961-.537 2.051-2.56 4.117a.198.198 0 00-.042.068 1.086 1.086 0 00-.274.635.879.879 0 00.25.693.871.871 0 00.693.25c.232-.016.453-.114.636-.273a.192.192 0 00.068-.043c2.066-2.021 3.156-2.306 4.118-2.558.73-.191 1.418-.372 2.34-1.293l10.3-10.298h.001l.003-.004.024-.024c.004-.004.005-.009.009-.012zM.919 18.607a.491.491 0 01-.39-.136.493.493 0 01-.136-.39.705.705 0 01.212-.446.19.19 0 00.034-.052c.325-.334.604-.642.848-.932l.863.863c-.29.244-.598.522-.93.846a.191.191 0 00-.054.035.701.701 0 01-.447.212zm4.749-2.862c-.748.196-1.641.433-3.013 1.52l-.919-.919c1.088-1.371 1.325-2.264 1.521-3.012.193-.734.344-1.314 1.191-2.16.005-.006.007-.013.012-.018l.543-.543.66.66a.195.195 0 10.277-.278l-.66-.66 1.213-1.211.66.66a.195.195 0 00.277 0 .196.196 0 000-.278l-.66-.66 1.212-1.21.66.659a.195.195 0 10.277-.277l-.66-.66L9.47 6.146l.66.66a.195.195 0 00.277 0 .196.196 0 000-.277l-.66-.66 1.213-1.212.66.66a.195.195 0 00.276 0 .196.196 0 000-.277l-.66-.66L12.45 3.17l.66.66a.195.195 0 10.276-.277l-.66-.66L13.94 1.68l.66.66a.195.195 0 10.277-.278l-.66-.66.53-.53h.001c.36-.359.87-.526 1.441-.47.585.057 1.158.34 1.616.798.931.931 1.08 2.3.33 3.052L7.83 14.554c-.847.846-1.427.998-2.162 1.19z"
        fill="#000"
      />
      <path
        d="M16.713 3.203a.196.196 0 00-.277 0l-9.632 9.63a.196.196 0 10.277.277l9.632-9.63a.196.196 0 000-.277z"
        fill="#000"
      />
    </svg>
  );
}

function FilterIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <line
        x1="0.5"
        y1="3.5"
        x2="19.5"
        y2="3.5"
        stroke="#414550"
        strokeLinecap="round"
      />
      <line
        x1="0.5"
        y1="10.1667"
        x2="19.5"
        y2="10.1667"
        stroke="#414550"
        strokeLinecap="round"
      />
      <line
        x1="0.5"
        y1="16.8333"
        x2="19.5"
        y2="16.8333"
        stroke="#414550"
        strokeLinecap="round"
      />
      <path
        d="M9.5 3.33333C9.5 4.92903 8.29484 6.16667 6.875 6.16667C5.45516 6.16667 4.25 4.92903 4.25 3.33333C4.25 1.73764 5.45516 0.5 6.875 0.5C8.29484 0.5 9.5 1.73764 9.5 3.33333Z"
        fill="#F5F6F7"
        stroke="#414550"
      />
      <path
        d="M18.25 10C18.25 11.5957 17.0448 12.8333 15.625 12.8333C14.2052 12.8333 13 11.5957 13 10C13 8.40431 14.2052 7.16667 15.625 7.16667C17.0448 7.16667 18.25 8.40431 18.25 10Z"
        fill="#F5F6F7"
        stroke="#414550"
      />
      <path
        d="M10.75 16.6667C10.75 18.2624 9.54484 19.5 8.125 19.5C6.70516 19.5 5.5 18.2624 5.5 16.6667C5.5 15.071 6.70516 13.8333 8.125 13.8333C9.54484 13.8333 10.75 15.071 10.75 16.6667Z"
        fill="#F5F6F7"
        stroke="#414550"
      />
    </svg>
  );
}

function FilterActiveIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="21"
      height="20"
      viewBox="0 0 21 20"
      fill="none"
    >
      <line
        x1="0.5"
        y1="3.5"
        x2="19.5"
        y2="3.5"
        stroke="#414550"
        strokeLinecap="round"
      />
      <line
        x1="0.5"
        y1="10.1666"
        x2="19.5"
        y2="10.1666"
        stroke="#414550"
        strokeLinecap="round"
      />
      <line
        x1="0.5"
        y1="16.8334"
        x2="19.5"
        y2="16.8334"
        stroke="#414550"
        strokeLinecap="round"
      />
      <path
        d="M9.5 3.33333C9.5 4.92903 8.29484 6.16667 6.875 6.16667C5.45516 6.16667 4.25 4.92903 4.25 3.33333C4.25 1.73764 5.45516 0.5 6.875 0.5C8.29484 0.5 9.5 1.73764 9.5 3.33333Z"
        fill="#F5F6F7"
        stroke="#414550"
      />
      <circle cx="16.5" cy="10" r="3.5" fill="#201255" stroke="#414550" />
      <path
        d="M10.75 16.6667C10.75 18.2624 9.54484 19.5 8.125 19.5C6.70516 19.5 5.5 18.2624 5.5 16.6667C5.5 15.071 6.70516 13.8334 8.125 13.8334C9.54484 13.8334 10.75 15.071 10.75 16.6667Z"
        fill="#F5F6F7"
        stroke="#414550"
      />
    </svg>
  );
}

function PromotionIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
    >
      <path
        d="M20 12V22H4V12"
        stroke="#FF595A"
        strokeOpacity="0.6"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M22 7H2V12H22V7Z"
        stroke="#FF595A"
        strokeOpacity="0.6"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M12 22V7"
        stroke="#FF595A"
        strokeOpacity="0.6"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M12 7H7.5C6.83696 7 6.20107 6.73661 5.73223 6.26777C5.26339 5.79893 5 5.16304 5 4.5C5 3.83696 5.26339 3.20107 5.73223 2.73223C6.20107 2.26339 6.83696 2 7.5 2C11 2 12 7 12 7Z"
        stroke="#FF595A"
        strokeOpacity="0.6"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M12 7H16.5C17.163 7 17.7989 6.73661 18.2678 6.26777C18.7366 5.79893 19 5.16304 19 4.5C19 3.83696 18.7366 3.20107 18.2678 2.73223C17.7989 2.26339 17.163 2 16.5 2C13 2 12 7 12 7Z"
        stroke="#FF595A"
        strokeOpacity="0.6"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CheckinIcon(_props: IconBaseProps) {
  return (
    <svg width={13} height={17} viewBox="0 0 13 17" fill="none">
      <mask id="a" fill="#fff">
        <path d="M9.158 12.183l-.46.545c1.92.244 3.261.798 3.261 1.442 0 .87-2.444 1.576-5.459 1.576-3.015 0-5.46-.706-5.46-1.576 0-.644 1.343-1.198 3.263-1.442l-.461-.545C1.693 12.551.2 13.348.2 14.275c0 1.275 2.82 2.31 6.299 2.31 3.479 0 6.3-1.035 6.3-2.31 0-.927-1.493-1.724-3.642-2.092zm2.622-6.278a5.28 5.28 0 00-10.56 0c0 2.916 5.28 8.8 5.28 8.8s5.28-5.884 5.28-8.8zm-5.28-4.4c2.43 0 4.4 1.995 4.4 4.455s-4.4 7.425-4.4 7.425S2.1 8.42 2.1 5.96s1.97-4.455 4.4-4.455zm2.2 3.96a2.2 2.2 0 10-4.4 0 2.2 2.2 0 004.4 0zm-3.52 0a1.32 1.32 0 112.64 0 1.32 1.32 0 01-2.64 0z" />
      </mask>
      <path
        d="M9.158 12.183l-.46.545c1.92.244 3.261.798 3.261 1.442 0 .87-2.444 1.576-5.459 1.576-3.015 0-5.46-.706-5.46-1.576 0-.644 1.343-1.198 3.263-1.442l-.461-.545C1.693 12.551.2 13.348.2 14.275c0 1.275 2.82 2.31 6.299 2.31 3.479 0 6.3-1.035 6.3-2.31 0-.927-1.493-1.724-3.642-2.092zm2.622-6.278a5.28 5.28 0 00-10.56 0c0 2.916 5.28 8.8 5.28 8.8s5.28-5.884 5.28-8.8zm-5.28-4.4c2.43 0 4.4 1.995 4.4 4.455s-4.4 7.425-4.4 7.425S2.1 8.42 2.1 5.96s1.97-4.455 4.4-4.455zm2.2 3.96a2.2 2.2 0 10-4.4 0 2.2 2.2 0 004.4 0zm-3.52 0a1.32 1.32 0 112.64 0 1.32 1.32 0 01-2.64 0z"
        fill="#fff"
        stroke="#fff"
        strokeWidth={2}
        mask="url(#a)"
      />
    </svg>
  );
}

function ScheduleIcon(props: IconBaseProps) {
  const { size } = props;

  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
      <path
        d="M12.667 2.667H3.333C2.597 2.667 2 3.264 2 4v9.333c0 .737.597 1.334 1.333 1.334h9.334c.736 0 1.333-.597 1.333-1.334V4c0-.736-.597-1.333-1.333-1.333zM10.667 1.333V4M5.333 1.333V4M2 6.667h12"
        stroke="#000"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function LocationPinIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 20 20"
      fill="none"
    >
      <path
        d="M17.5455 8.77273C17.5455 14.8182 9.77273 20 9.77273 20C9.77273 20 2 14.8182 2 8.77273C2 6.71127 2.81891 4.73425 4.27658 3.27658C5.73425 1.81891 7.71127 1 9.77273 1C11.8342 1 13.8112 1.81891 15.2689 3.27658C16.7265 4.73425 17.5455 6.71127 17.5455 8.77273Z"
        stroke="black"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M9.77279 11.3637C11.2037 11.3637 12.3637 10.2037 12.3637 8.77279C12.3637 7.34187 11.2037 6.18188 9.77279 6.18188C8.34187 6.18188 7.18188 7.34187 7.18188 8.77279C7.18188 10.2037 8.34187 11.3637 9.77279 11.3637Z"
        stroke="black"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function MapPinIcon({ fill = "#5941b0" }: { fill?: string }) {
  return (
    <svg
      fill={fill}
      height="32"
      viewBox="0 0 22 32"
      width="22"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fill={fill}
        d="m11 0c-2.91738 0-5.71527 1.14628-7.77817 3.18668s-3.22183 4.80777-3.22183 7.69332c0 6.3704 6.44911 16.5077 9.47087 20.3743.18038.2318.41213.4196.67743.5488.2653.1293.557.1967.8527.1969.2958.0002.5876-.0667.8531-.1956s.4975-.3163.6783-.5478c3.0235-3.8565 9.4676-13.9678 9.4676-20.3766 0-1.42878-.2845-2.84357-.8373-4.16359-.5528-1.32003-1.3631-2.51943-2.3845-3.52973-1.0215-1.0103-2.2341-1.81172-3.5687-2.358489-1.3346-.546771-2.765-.828191-4.2095-.828191zm0 14.08c-.8958 0-1.77155-.2627-2.51641-.755-.74485-.4923-1.3254-1.192-1.66822-2.0106s-.43252-1.71936-.25775-2.5884c.17477-.86903.60615-1.66729 1.2396-2.29383s1.44052-1.05322 2.31918-1.22608c.8786-.17287 1.7893-.08415 2.6169.25493.8277.33908 1.5351.9133 2.0328 1.65003s.7633 1.60289.7633 2.48896c0 1.18819-.4772 2.32769-1.3266 3.16779-.8495.8402-2.0015 1.3122-3.2028 1.3122z"
      />
    </svg>
  );
}

function MapPinIconFilled({ fill }: { fill?: string }) {
  return (
    <svg
      width="22"
      height="32"
      viewBox="0 0 22 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M11 0C8.08262 0 5.28473 1.14628 3.22183 3.18668C1.15893 5.22708 0 7.99445 0 10.88C0 17.2504 6.44911 27.3877 9.47087 31.2543C9.65125 31.4861 9.883 31.6739 10.1483 31.8031C10.4136 31.9324 10.7053 31.9998 11.001 32C11.2968 32.0002 11.5886 31.9333 11.8541 31.8044C12.1196 31.6755 12.3516 31.4881 12.5324 31.2566C15.5559 27.4001 22 17.2888 22 10.88C22 9.45122 21.7155 8.03643 21.1627 6.71641C20.6099 5.39638 19.7996 4.19698 18.7782 3.18668C17.7567 2.17638 16.5441 1.37496 15.2095 0.828191C13.8749 0.28142 12.4445 0 11 0ZM11 14.08C10.1042 14.08 9.22845 13.8173 8.48359 13.325C7.73874 12.8327 7.15819 12.133 6.81537 11.3144C6.47255 10.4958 6.38285 9.59504 6.55762 8.726C6.73239 7.85697 7.16377 7.05871 7.79722 6.43217C8.43067 5.80563 9.23774 5.37895 10.1164 5.20609C10.995 5.03322 11.9057 5.12194 12.7333 5.46102C13.561 5.8001 14.2684 6.37432 14.7661 7.11105C15.2638 7.84778 15.5294 8.71394 15.5294 9.60001C15.5294 10.7882 15.0522 11.9277 14.2028 12.7678C13.3533 13.608 12.2013 14.08 11 14.08Z"
        fill={fill || "#534588"}
      />
      <circle cx="11" cy="11" r="8" fill="white" />
    </svg>
  );
}

function IconClose() {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g filter="url(#filter0_b_1743_1893)">
        <rect width="24" height="24" rx="12" fill="#F2F2F7" />
        <path
          d="M8.03896 15.3448C7.79014 15.5936 7.78506 16.0354 8.04404 16.2944C8.30811 16.5534 8.7499 16.5483 8.99365 16.3045L11.9999 13.2983L15.0011 16.2995C15.255 16.5534 15.6917 16.5534 15.9507 16.2944C16.2097 16.0303 16.2097 15.5987 15.9558 15.3448L12.9546 12.3436L15.9558 9.33736C16.2097 9.08345 16.2147 8.64673 15.9507 8.38775C15.6917 8.12876 15.255 8.12876 15.0011 8.38267L11.9999 11.3838L8.99365 8.38267C8.7499 8.13384 8.30303 8.12369 8.04404 8.38775C7.78506 8.64673 7.79014 9.09361 8.03896 9.33736L11.0401 12.3436L8.03896 15.3448Z"
          fill="#06001E"
          fillOpacity="0.7"
        />
      </g>
      <defs>
        <filter
          id="filter0_b_1743_1893"
          x="-54.3656"
          y="-54.3656"
          width="132.731"
          height="132.731"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feGaussianBlur in="BackgroundImage" stdDeviation="27.1828" />
          <feComposite
            in2="SourceAlpha"
            operator="in"
            result="effect1_backgroundBlur_1743_1893"
          />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="effect1_backgroundBlur_1743_1893"
            result="shape"
          />
        </filter>
      </defs>
    </svg>
  );
}

function GlobeIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
    >
      <path
        d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
        stroke="black"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M2 12H22"
        stroke="black"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M12 2C14.5013 4.73835 15.9228 8.29203 16 12C15.9228 15.708 14.5013 19.2616 12 22C9.49872 19.2616 8.07725 15.708 8 12C8.07725 8.29203 9.49872 4.73835 12 2V2Z"
        stroke="black"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CityIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
    >
      <path
        d="M11.6667 7.48087L19.0083 8.95025C19.6 9.0762 20 9.57999 20 10.1761V18.7405C20 19.4374 19.4417 20 18.75 20H11.25C11.4833 20 11.6667 19.8153 11.6667 19.5802V19.1604H18.75C18.975 19.1604 19.1667 18.9756 19.1667 18.7405V10.1761C19.1667 9.98302 19.0333 9.80669 18.8417 9.76471L11.6667 8.33731V7.48087Z"
        fill="#414550"
      />
      <path
        d="M16.25 11.6036C16.4833 11.6036 16.6667 11.7883 16.6667 12.0234C16.6667 12.2585 16.4833 12.4432 16.25 12.4432H14.5833C14.35 12.4432 14.1667 12.2585 14.1667 12.0234C14.1667 11.7883 14.35 11.6036 14.5833 11.6036H16.25Z"
        fill="#414550"
      />
      <path
        d="M16.25 14.1225C16.4833 14.1225 16.6667 14.3072 16.6667 14.5423C16.6667 14.7774 16.4833 14.9621 16.25 14.9621H14.5833C14.35 14.9621 14.1667 14.7774 14.1667 14.5423C14.1667 14.3072 14.35 14.1225 14.5833 14.1225H16.25Z"
        fill="#414550"
      />
      <path
        d="M16.25 16.6413C16.4833 16.6413 16.6667 16.8261 16.6667 17.0612C16.6667 17.2963 16.4833 17.481 16.25 17.481H14.5833C14.35 17.481 14.1667 17.2963 14.1667 17.0612C14.1667 16.8261 14.35 16.6413 14.5833 16.6413H16.25Z"
        fill="#414550"
      />
      <path
        d="M11.6666 19.5801C11.6666 19.8152 11.4833 20 11.25 20C11.0167 20 10.8333 19.8152 10.8333 19.5801V19.1603V7.82503C10.8333 7.69908 10.8917 7.58153 10.9833 7.49757C11.0833 7.422 11.2083 7.38841 11.3333 7.4136L11.6666 7.48077V8.33722V19.1603V19.5801Z"
        fill="#414550"
      />
      <path
        d="M10.8333 19.1603V19.5801C10.8333 19.8152 11.0167 20 11.25 20H7.91667C8.15 20 8.33333 19.8152 8.33333 19.5801V19.1603H10.8333Z"
        fill="#414550"
      />
      <path
        d="M8.75 4.04664C8.98333 4.04664 9.16667 4.23137 9.16667 4.46647C9.16667 4.70157 8.98333 4.8863 8.75 4.8863H7.08333C6.85 4.8863 6.66667 4.70157 6.66667 4.46647C6.66667 4.23137 6.85 4.04664 7.08333 4.04664H8.75Z"
        fill="#414550"
      />
      <path
        d="M9.16667 6.98551C9.16667 7.22062 8.98333 7.40534 8.75 7.40534H7.08333C6.85 7.40534 6.66667 7.22062 6.66667 6.98551C6.66667 6.75041 6.85 6.56569 7.08333 6.56569H8.75C8.98333 6.56569 9.16667 6.75041 9.16667 6.98551Z"
        fill="#414550"
      />
      <path
        d="M8.75 9.08452C8.98333 9.08452 9.16667 9.26924 9.16667 9.50434C9.16667 9.73944 8.98333 9.92415 8.75 9.92415H7.08333C6.85 9.92415 6.66667 9.73944 6.66667 9.50434C6.66667 9.26924 6.85 9.08452 7.08333 9.08452H8.75Z"
        fill="#414550"
      />
      <path
        d="M8.75 11.6036C8.98333 11.6036 9.16667 11.7883 9.16667 12.0234C9.16667 12.2585 8.98333 12.4432 8.75 12.4432H7.08333C6.85 12.4432 6.66667 12.2585 6.66667 12.0234C6.66667 11.7883 6.85 11.6036 7.08333 11.6036H8.75Z"
        fill="#414550"
      />
      <path
        d="M5 12.0234C5 12.2585 4.81667 12.4432 4.58333 12.4432H2.91667C2.68333 12.4432 2.5 12.2585 2.5 12.0234C2.5 11.7883 2.68333 11.6036 2.91667 11.6036H4.58333C4.81667 11.6036 5 11.7883 5 12.0234Z"
        fill="#414550"
      />
      <path
        d="M4.58333 4.04664C4.81667 4.04664 5 4.23137 5 4.46647C5 4.70157 4.81667 4.8863 4.58333 4.8863H2.91667C2.68333 4.8863 2.5 4.70157 2.5 4.46647C2.5 4.23137 2.68333 4.04664 2.91667 4.04664H4.58333Z"
        fill="#414550"
      />
      <path
        d="M4.58333 6.56569C4.81667 6.56569 5 6.75041 5 6.98551C5 7.22062 4.81667 7.40534 4.58333 7.40534H2.91667C2.68333 7.40534 2.5 7.22062 2.5 6.98551C2.5 6.75041 2.68333 6.56569 2.91667 6.56569H4.58333Z"
        fill="#414550"
      />
      <path
        d="M4.58333 9.08452C4.81667 9.08452 5 9.26924 5 9.50434C5 9.73944 4.81667 9.92415 4.58333 9.92415H2.91667C2.68333 9.92415 2.5 9.73944 2.5 9.50434C2.5 9.26924 2.68333 9.08452 2.91667 9.08452H4.58333Z"
        fill="#414550"
      />
      <path
        d="M7.5 15.382C7.5 15.1469 7.30833 14.9621 7.08333 14.9621H4.58333C4.35 14.9621 4.16667 15.1469 4.16667 15.382V19.1604H3.33333V15.382C3.33333 14.6851 3.89167 14.1225 4.58333 14.1225H7.08333C7.775 14.1225 8.33333 14.6851 8.33333 15.382V19.1604H7.5V15.382Z"
        fill="#414550"
      />
      <path
        d="M4.16667 19.1603H7.5H8.33333V19.5801C8.33333 19.8152 8.15 20 7.91667 20H3.75C3.51667 20 3.33333 19.8152 3.33333 19.5801V19.1603H4.16667Z"
        fill="#414550"
      />
      <path
        d="M1.45833 0.0163471L10.6167 1.41856C11.225 1.51931 11.6667 2.0399 11.6667 2.66123V7.48081L11.3333 7.41363C11.2083 7.38844 11.0833 7.42203 10.9833 7.4976C10.8917 7.58156 10.8333 7.69911 10.8333 7.82506V2.66123C10.8333 2.45132 10.6833 2.275 10.4833 2.24141L1.325 0.847597C1.3 0.839201 1.275 0.839201 1.25 0.839201C1.15 0.839201 1.05833 0.872787 0.983333 0.939958C0.883333 1.02392 0.833333 1.13308 0.833333 1.25902V18.7405C0.833333 18.9756 1.025 19.1603 1.25 19.1603H3.33333V19.5801C3.33333 19.8152 3.51667 19.9999 3.75 19.9999H1.25C0.558333 19.9999 0 19.4374 0 18.7405V1.25902C0 0.88958 0.158333 0.536928 0.441667 0.301827C0.725 0.0583294 1.09167 -0.0424282 1.45833 0.0163471Z"
        fill="#414550"
      />
    </svg>
  );
}

function BottomBarNavigationReservationsIcon() {
  return (
    <svg
      width="23"
      height="23"
      viewBox="0 0 23 23"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M22.08 1.84H17.48V0.46C17.48 0.338 17.4315 0.220998 17.3453 0.134731C17.259 0.0484642 17.142 0 17.02 0C16.898 0 16.781 0.0484642 16.6947 0.134731C16.6085 0.220998 16.56 0.338 16.56 0.46V1.84H6.44V0.46C6.44 0.338 6.39154 0.220998 6.30527 0.134731C6.219 0.0484642 6.102 0 5.98 0C5.858 0 5.741 0.0484642 5.65473 0.134731C5.56846 0.220998 5.52 0.338 5.52 0.46V1.84H0.92C0.676001 1.84 0.441995 1.93693 0.269462 2.10946C0.0969283 2.282 0 2.516 0 2.76L0 21.16C0 21.404 0.0969283 21.638 0.269462 21.8105C0.441995 21.9831 0.676001 22.08 0.92 22.08H22.08C22.324 22.08 22.558 21.9831 22.7305 21.8105C22.9031 21.638 23 21.404 23 21.16V2.76C23 2.516 22.9031 2.282 22.7305 2.10946C22.558 1.93693 22.324 1.84 22.08 1.84ZM22.08 21.16H0.92V7.36H22.08V21.16ZM22.08 6.44H0.92V2.76H22.08V6.44Z"
        fill="#747880"
      />
      <path
        d="M10.2856 16.4533C10.3738 16.5268 10.4864 16.5647 10.6012 16.5594C10.7159 16.5541 10.8245 16.5061 10.9057 16.4248L15.5057 11.8248C15.5895 11.738 15.6358 11.6218 15.6348 11.5012C15.6337 11.3806 15.5854 11.2652 15.5001 11.1799C15.4148 11.0946 15.2994 11.0463 15.1788 11.0452C15.0582 11.0442 14.942 11.0905 14.8552 11.1743L10.5524 15.4772L8.11438 13.4467C8.06793 13.408 8.0143 13.3788 7.95657 13.3608C7.89883 13.3428 7.83812 13.3364 7.77789 13.3418C7.71766 13.3473 7.6591 13.3646 7.60555 13.3927C7.552 13.4208 7.50451 13.4591 7.46578 13.5056C7.42706 13.5521 7.39787 13.6057 7.37987 13.6634C7.36188 13.7212 7.35543 13.7819 7.36089 13.8421C7.36636 13.9023 7.38364 13.9609 7.41174 14.0144C7.43984 14.068 7.47821 14.1155 7.52466 14.1542L10.2856 16.4533Z"
        fill="#747880"
      />
    </svg>
  );
}

function BottomBarNavigationHomeIcon() {
  return (
    <svg
      width="23"
      height="23"
      viewBox="0 0 23 23"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath="url(#clip0_125_3612)">
        <path
          d="M22.8508 11.1604L11.8108 1.04043C11.7259 0.962837 11.615 0.919815 11.5 0.919815C11.3849 0.919815 11.274 0.962837 11.1891 1.04043L0.149083 11.1604C0.059133 11.2429 0.00561888 11.3577 0.000313474 11.4796C-0.00499193 11.6015 0.0383459 11.7205 0.120793 11.8105C0.203241 11.9004 0.318044 11.9539 0.439947 11.9592C0.561851 11.9645 0.680869 11.9212 0.770819 11.8387L2.76004 10.0153V19.78C2.76004 20.146 2.90544 20.497 3.16424 20.7558C3.42304 21.0146 3.77404 21.16 4.14004 21.16H18.86C19.226 21.16 19.5771 21.0146 19.8359 20.7558C20.0947 20.497 20.24 20.146 20.24 19.78V10.0158L22.2291 11.8387C22.319 11.9211 22.4381 11.9644 22.56 11.9591C22.6819 11.9538 22.7967 11.9003 22.8791 11.8104C22.9616 11.7204 23.0049 11.6014 22.9996 11.4795C22.9943 11.3576 22.9408 11.2428 22.8508 11.1603V11.1604ZM19.32 9.2V19.78C19.32 19.902 19.2716 20.019 19.1853 20.1053C19.099 20.1915 18.982 20.24 18.86 20.24H4.14004C4.01804 20.24 3.90104 20.1915 3.81477 20.1053C3.72851 20.019 3.68004 19.902 3.68004 19.78V9.2H3.64968L11.5 2.00348L19.3503 9.2H19.32Z"
          fill="#747880"
        />
      </g>
      <defs>
        <clipPath id="clip0_125_3612">
          <rect width="23" height="23" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
}

function BottomBarNavigationSupportIcon() {
  return (
    <svg
      width="23"
      height="23"
      viewBox="0 0 23 23"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M21.62 0H1.38C1.014 0 0.662993 0.145392 0.404193 0.404193C0.145392 0.662993 0 1.014 0 1.38L0 16.1C0 16.466 0.145392 16.817 0.404193 17.0758C0.662993 17.3346 1.014 17.48 1.38 17.48H13.03C13.1313 17.4793 13.2301 17.5116 13.3115 17.572L17.8434 20.976C17.9801 21.0798 18.1431 21.1434 18.3139 21.1594C18.4848 21.1755 18.6568 21.1434 18.8104 21.0669C18.964 20.9903 19.0932 20.8723 19.1832 20.7262C19.2733 20.58 19.3206 20.4116 19.32 20.24V17.48H21.62C21.986 17.48 22.337 17.3346 22.5958 17.0758C22.8546 16.817 23 16.466 23 16.1V1.38C23 1.014 22.8546 0.662993 22.5958 0.404193C22.337 0.145392 21.986 0 21.62 0V0ZM22.08 16.1C22.08 16.222 22.0315 16.339 21.9453 16.4253C21.859 16.5115 21.742 16.56 21.62 16.56H19.044C18.8732 16.56 18.7094 16.6278 18.5886 16.7486C18.4678 16.8694 18.4 17.0332 18.4 17.204V20.24L13.8681 16.8443C13.6272 16.6607 13.3329 16.5609 13.03 16.56H1.38C1.258 16.56 1.141 16.5115 1.05473 16.4253C0.968464 16.339 0.92 16.222 0.92 16.1V1.38C0.92 1.258 0.968464 1.141 1.05473 1.05473C1.141 0.968464 1.258 0.92 1.38 0.92H21.62C21.742 0.92 21.859 0.968464 21.9453 1.05473C22.0315 1.141 22.08 1.258 22.08 1.38V16.1Z"
        fill="#747880"
      />
      <path
        d="M11.5 3.68C10.646 3.68 9.82701 4.01925 9.22315 4.62312C8.61928 5.22698 8.28003 6.046 8.28003 6.9C8.28003 7.022 8.32849 7.139 8.41476 7.22527C8.50103 7.31154 8.61803 7.36 8.74003 7.36C8.86203 7.36 8.97903 7.31154 9.0653 7.22527C9.15157 7.139 9.20003 7.022 9.20003 6.9C9.20003 6.29 9.44235 5.70499 9.87368 5.27365C10.305 4.84232 10.89 4.6 11.5 4.6C12.11 4.6 12.695 4.84232 13.1264 5.27365C13.5577 5.70499 13.8 6.29 13.8 6.9V7.41428C13.8001 7.70384 13.709 7.98607 13.5398 8.22103C13.3705 8.45598 13.1317 8.63175 12.857 8.72344L11.9839 9.01416C11.7088 9.1058 11.4696 9.28177 11.3001 9.51711C11.1307 9.75244 11.0397 10.0352 11.04 10.3252V11.5C11.04 11.622 11.0885 11.739 11.1748 11.8253C11.261 11.9115 11.378 11.96 11.5 11.96C11.622 11.96 11.739 11.9115 11.8253 11.8253C11.9116 11.739 11.96 11.622 11.96 11.5V10.3252C11.9601 10.2287 11.9905 10.1346 12.047 10.0564C12.1035 9.97809 12.1831 9.91957 12.2747 9.88908L13.1477 9.59836C13.6057 9.44562 14.0039 9.15268 14.2861 8.76102C14.5683 8.36936 14.7201 7.89885 14.72 7.41612V6.9C14.72 6.046 14.3808 5.22698 13.7769 4.62312C13.173 4.01925 12.354 3.68 11.5 3.68Z"
        fill="#747880"
      />
      <path d="M11.96 12.8818H11.04V13.8018H11.96V12.8818Z" fill="#747880" />
    </svg>
  );
}

function BottomBarNavigationProfileIcon() {
  return (
    <svg
      width="23"
      height="23"
      viewBox="0 0 23 23"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath="url(#clip0_125_3633)">
        <path
          d="M11.5 3.68C10.6812 3.68 9.88075 3.92281 9.19993 4.37772C8.51911 4.83263 7.98847 5.4792 7.67513 6.23569C7.36178 6.99218 7.27979 7.82459 7.43954 8.62767C7.59928 9.43076 7.99358 10.1684 8.57257 10.7474C9.15156 11.3264 9.88923 11.7207 10.6923 11.8805C11.4954 12.0402 12.3278 11.9582 13.0843 11.6449C13.8408 11.3315 14.4874 10.8009 14.9423 10.1201C15.3972 9.43924 15.64 8.63881 15.64 7.82C15.64 6.722 15.2038 5.66898 14.4274 4.89258C13.651 4.11618 12.598 3.68 11.5 3.68ZM11.5 11.04C10.8631 11.04 10.2406 10.8512 9.71105 10.4973C9.18152 10.1435 8.76881 9.64062 8.5251 9.05224C8.28138 8.46386 8.21761 7.81643 8.34186 7.19181C8.4661 6.56719 8.77278 5.99344 9.2231 5.54312C9.67343 5.09279 10.2472 4.78612 10.8718 4.66187C11.4964 4.53763 12.1438 4.60139 12.7322 4.84511C13.3206 5.08882 13.8235 5.50154 14.1773 6.03106C14.5311 6.56059 14.72 7.18314 14.72 7.82C14.72 8.674 14.3807 9.49302 13.7769 10.0969C13.173 10.7008 12.354 11.04 11.5 11.04Z"
          fill="#747880"
        />
        <path
          d="M11.5 1.1403e-06C9.0873 -0.0019965 6.73519 0.755559 4.77716 2.16526C2.81912 3.57496 1.35454 5.56527 0.591058 7.85399C-0.172426 10.1427 -0.196063 12.6137 0.523497 14.9166C1.24306 17.2195 2.6693 19.2375 4.6 20.6844C6.58655 22.1869 9.00924 22.9999 11.5 22.9999C13.9908 22.9999 16.4135 22.1869 18.4 20.6844C18.7067 20.4537 19.0008 20.2084 19.2823 19.9484C20.9868 18.3826 22.1786 16.3385 22.7016 14.0838C23.2246 11.8291 23.0543 9.46904 22.2131 7.31278C21.3719 5.15653 19.899 3.30465 17.9874 1.99976C16.0757 0.694879 13.8145 -0.00214803 11.5 1.1403e-06ZM11.5 22.08C9.01043 22.0823 6.6006 21.2022 4.69844 19.596C4.95967 17.9803 5.78754 16.5103 7.0338 15.4494C8.28006 14.3884 9.8633 13.8058 11.5 13.8058C13.1367 13.8058 14.7199 14.3884 15.9662 15.4494C17.2125 16.5103 18.0403 17.9803 18.3016 19.596C16.3994 21.2022 13.9896 22.0823 11.5 22.08ZM19.09 18.8545C18.6788 17.1513 17.7067 15.6358 16.33 14.552C14.9534 13.4681 13.2522 12.8788 11.5 12.8788C9.74786 12.8788 8.04663 13.4681 6.66996 14.552C5.29328 15.6358 4.32118 17.1513 3.91 18.8545C2.4614 17.3628 1.48446 15.4773 1.10124 13.4336C0.718019 11.3899 0.945507 9.27858 1.75528 7.36342C2.56506 5.44825 3.92122 3.81416 5.65433 2.66529C7.38745 1.51642 9.42068 0.903708 11.5 0.903708C13.5793 0.903708 15.6126 1.51642 17.3457 2.66529C19.0788 3.81416 20.4349 5.44825 21.2447 7.36342C22.0545 9.27858 22.282 11.3899 21.8988 13.4336C21.5155 15.4773 20.5386 17.3628 19.09 18.8545Z"
          fill="#747880"
        />
      </g>
      <defs>
        <clipPath id="clip0_125_3633">
          <rect width="23" height="23" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
}

function BottomBarNavigationReportingIcon() {
  return (
    <svg
      width="23"
      height="23"
      viewBox="0 0 23 23"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath="url(#clip0_211_32178)">
        <path
          d="M11.5 0C9.22552 0 7.00211 0.674463 5.11095 1.9381C3.21978 3.20174 1.7458 4.99779 0.875391 7.09914C0.00498275 9.20049 -0.222756 11.5128 0.220974 13.7435C0.664705 15.9743 1.75997 18.0234 3.36828 19.6317C4.97658 21.24 7.02569 22.3353 9.25647 22.779C11.4872 23.2228 13.7995 22.995 15.9009 22.1246C18.0022 21.2542 19.7983 19.7802 21.0619 17.8891C22.3255 15.9979 23 13.7745 23 11.5C23 8.45001 21.7884 5.52494 19.6317 3.36827C17.4751 1.2116 14.55 0 11.5 0V0ZM22.08 11.5C22.0809 13.657 21.4183 15.7621 20.182 17.5297L11.96 11.7217V0.943C14.6815 1.06144 17.2525 2.22505 19.1376 4.19155C21.0227 6.15806 22.0767 8.7759 22.08 11.5ZM11.04 0.943V11.6049L1.27053 14.1533C0.866846 12.6283 0.811132 11.0321 1.1075 9.48267C1.40388 7.93326 2.04475 6.47029 2.98274 5.20194C3.92073 3.9336 5.13183 2.89233 6.52648 2.15514C7.92114 1.41794 9.46367 1.00369 11.04 0.943ZM11.5 22.08C9.31585 22.0761 7.18634 21.3969 5.40328 20.1354C3.62022 18.874 2.27085 17.092 1.54009 15.0337L11.4089 12.4577L19.6264 18.2629C18.6355 19.4571 17.3936 20.4183 15.989 21.078C14.5845 21.7378 13.0518 22.0799 11.5 22.08Z"
          fill="#534588"
        />
      </g>
      <defs>
        <clipPath id="clip0_211_32178">
          <rect width="23" height="23" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
}

function ReportingDateRangePickerIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="8" cy="8" r="8" fill="#06001E" fillOpacity="0.1" />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M8.64575 11.6458L12.4706 7.82099C12.8871 7.40443 12.8871 6.72906 12.4706 6.3125C12.2706 6.11246 11.9992 6.00008 11.7163 6.00008H4.06667C3.47756 6.00008 3 6.47764 3 7.06675C3 7.34964 3.11238 7.62096 3.31242 7.82099L7.13726 11.6458C7.55382 12.0624 8.22919 12.0624 8.64575 11.6458Z"
        fill="#06001E"
        fillOpacity="0.5"
      />
    </svg>
  );
}

function LockedIcon(props: IconBaseProps) {
  const { size } = props;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M12.125 5.25H12.6554C14.1785 5.25 15.4202 6.4482 15.4963 7.95599L15.5 8.10284V15.1472C15.5 16.6726 14.3049 17.9198 12.8018 17.9963L12.6554 18H3.34456C1.82154 18 0.579818 16.8018 0.503698 15.294L0.5 15.1472V8.10284C0.5 6.57737 1.69512 5.33017 3.19817 5.25371L3.34456 5.25C11.5339 5.25 12.125 5.25 12.125 5.25ZM12.6554 6.75H3.34456C2.6402 6.75 2.06066 7.29633 2.00447 7.99184L2 8.10284V15.1472C2 15.8585 2.54329 16.4392 3.23429 16.4955L3.34456 16.5H12.6554C13.3598 16.5 13.9393 15.9537 13.9955 15.2582L14 15.1472V8.10284C14 7.39148 13.4567 6.81077 12.7657 6.75447L12.6554 6.75ZM8 8.625C9.65685 8.625 11 9.96815 11 11.625C11 13.2819 9.65685 14.625 8 14.625C6.34315 14.625 5 13.2819 5 11.625C5 9.96815 6.34315 8.625 8 8.625ZM8 10.125C7.17157 10.125 6.5 10.7966 6.5 11.625C6.5 12.4534 7.17157 13.125 8 13.125C8.82843 13.125 9.5 12.4534 9.5 11.625C9.5 10.7966 8.82843 10.125 8 10.125Z"
        fill="white"
      />
      <path
        d="M8 0C5.77444 0 3.9652 1.75439 3.87827 3.96354L3.87823 3.96537C3.87609 4.02001 3.875 4.07491 3.875 4.13006V6.75H5.375V4.13006C5.375 2.72485 6.48203 1.5677 7.87626 1.50287L8 1.50018L8.12374 1.50287C9.51797 1.5677 10.625 2.72485 10.625 4.13006V6.75H12.125V4.13006C12.125 4.07491 12.1239 4.02001 12.1218 3.96537L12.1217 3.96354C12.0348 1.75439 10.2256 0 8 0Z"
        fill="white"
      />
    </svg>
  );
}

function SuccessCheckIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M15.36 8.31964C15.3592 9.96924 14.8269 11.5747 13.8422 12.8981C12.8574 14.2215 11.4725 15.1925 9.89271 15.6672C8.31287 16.1419 6.62215 16.095 5.07106 15.5335C3.51996 14.972 2.19101 13.9257 1.28112 12.5497C0.371231 11.1738 -0.0711936 9.5413 0.0194016 7.89418C0.109997 6.24707 0.728792 4.67296 1.7841 3.40508C2.83941 2.1372 4.27509 1.24302 5.87841 0.855011C7.48173 0.467005 9.16739 0.605825 10.6856 1.2509C10.7637 1.28422 10.8254 1.34719 10.857 1.42596C10.8887 1.50473 10.8877 1.59285 10.8544 1.67093C10.8211 1.74901 10.7581 1.81066 10.6793 1.84231C10.6006 1.87396 10.5125 1.87302 10.4344 1.8397C8.97528 1.21446 7.34824 1.09841 5.81518 1.51023C4.28212 1.92204 2.93223 2.83778 1.98285 4.11C1.03346 5.38222 0.539813 6.9369 0.581368 8.52377C0.622924 10.1106 1.19727 11.6374 2.21194 12.8581C3.22661 14.0789 4.62257 14.9228 6.17508 15.2538C7.7276 15.5848 9.34634 15.3837 10.7707 14.683C12.1951 13.9822 13.3422 12.8226 14.0274 11.3906C14.7126 9.95873 14.896 8.33789 14.5481 6.78908C14.5297 6.70619 14.545 6.61939 14.5906 6.54778C14.6362 6.47616 14.7084 6.42559 14.7912 6.40719C14.8741 6.38879 14.9609 6.40407 15.0325 6.44966C15.1042 6.49526 15.1547 6.56744 15.1731 6.65032C15.2957 7.19834 15.3584 7.75807 15.36 8.31964ZM3.71937 7.74901C3.68646 7.72283 3.64871 7.70339 3.60828 7.6918C3.56786 7.68021 3.52554 7.67669 3.48376 7.68145C3.44197 7.68621 3.40154 7.69915 3.36475 7.71954C3.32797 7.73993 3.29556 7.76736 3.26938 7.80028C3.2432 7.83319 3.22376 7.87093 3.21217 7.91136C3.20058 7.95179 3.19706 7.9941 3.20182 8.03589C3.20658 8.07767 3.21953 8.11811 3.23991 8.15489C3.2603 8.19167 3.28774 8.22408 3.32065 8.25026C4.25191 8.98997 7.12442 11.4196 7.15316 11.4439C7.18883 11.474 7.23068 11.496 7.27577 11.5083C7.32085 11.5206 7.36808 11.5228 7.41412 11.5149C7.46017 11.507 7.50392 11.4891 7.54231 11.4624C7.58069 11.4358 7.61277 11.401 7.6363 11.3607C7.67566 11.2938 11.5676 4.64943 14.6169 1.83439C14.6496 1.80641 14.6763 1.77212 14.6954 1.73358C14.7145 1.69504 14.7257 1.65304 14.7282 1.61009C14.7307 1.56715 14.7246 1.52413 14.7101 1.48361C14.6956 1.4431 14.6731 1.40592 14.6439 1.37431C14.6148 1.34269 14.5795 1.31728 14.5403 1.2996C14.5011 1.28191 14.4587 1.27232 14.4157 1.27139C14.3727 1.27047 14.3299 1.27822 14.29 1.2942C14.25 1.31017 14.2137 1.33404 14.1832 1.36437C11.4219 3.91279 8.11316 9.31189 7.27758 10.7106C6.51873 10.0709 4.48065 8.35432 3.71937 7.74901Z"
        fill="#00947E"
      />
    </svg>
  );
}

function PencilIcon(props: IconBaseProps) {
  const { color } = props;
  return (
    <svg
      width="24"
      height="25"
      viewBox="0 0 24 25"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M13.4851 9.85111L14.3572 10.6689L5.76821 18.7222H4.89603V17.9044L13.4851 9.85111ZM16.8979 4.5C16.6609 4.5 16.4144 4.58889 16.2343 4.75778L14.4994 6.38444L18.0545 9.71778L19.7894 8.09111C20.1591 7.74444 20.1591 7.18444 19.7894 6.83778L17.571 4.75778C17.3814 4.58 17.1444 4.5 16.8979 4.5ZM13.4851 7.33556L3 17.1667V20.5H6.55506L17.0401 10.6689L13.4851 7.33556Z"
        fill={color || colors.brand.blackcore}
      />
    </svg>
  );
}

function ButtomNavigationTeamSpacesIcon() {
  return (
    <svg
      width="23"
      height="23"
      viewBox="0 0 23 23"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M11.04 6.43992C11.5859 6.43992 12.1195 6.27805 12.5734 5.97478C13.0273 5.67151 13.381 5.24045 13.5899 4.73613C13.7988 4.23181 13.8535 3.67686 13.747 3.14147C13.6405 2.60609 13.3776 2.1143 12.9916 1.72831C12.6057 1.34232 12.1139 1.07945 11.5785 0.972956C11.0431 0.866461 10.4881 0.921118 9.98382 1.13002C9.4795 1.33891 9.04845 1.69267 8.74517 2.14655C8.4419 2.60043 8.28003 3.13405 8.28003 3.67992C8.28003 4.41192 8.57081 5.11394 9.08841 5.63154C9.60601 6.14914 10.308 6.43992 11.04 6.43992ZM11.04 1.83992C11.4039 1.83992 11.7597 1.94784 12.0623 2.15002C12.3649 2.3522 12.6007 2.63957 12.74 2.97579C12.8792 3.312 12.9157 3.68196 12.8447 4.03889C12.7737 4.39581 12.5984 4.72367 12.3411 4.981C12.0838 5.23833 11.7559 5.41357 11.399 5.48457C11.0421 5.55557 10.6721 5.51913 10.3359 5.37986C9.99968 5.2406 9.71231 5.00476 9.51013 4.70217C9.30794 4.39959 9.20003 4.04384 9.20003 3.67992C9.20003 3.19192 9.39389 2.72391 9.73895 2.37885C10.084 2.03378 10.552 1.83992 11.04 1.83992Z"
        fill="#534588"
      />
      <path
        d="M17.48 5.52C18.0258 5.52 18.5595 5.35813 19.0133 5.05486C19.4672 4.75159 19.821 4.32053 20.0299 3.81621C20.2388 3.31188 20.2934 2.75694 20.1869 2.22155C20.0804 1.68616 19.8176 1.19438 19.4316 0.808387C19.0456 0.422394 18.5538 0.159529 18.0184 0.0530339C17.483 -0.0534613 16.9281 0.00119587 16.4238 0.210094C15.9194 0.418992 15.4884 0.772748 15.1851 1.22663C14.8818 1.68051 14.72 2.21413 14.72 2.76C14.72 3.492 15.0108 4.19402 15.5284 4.71162C16.046 5.22922 16.748 5.52 17.48 5.52ZM17.48 0.920001C17.8439 0.920001 18.1996 1.02792 18.5022 1.2301C18.8048 1.43228 19.0406 1.71965 19.1799 2.05586C19.3192 2.39208 19.3556 2.76204 19.2846 3.11897C19.2136 3.47589 19.0384 3.80375 18.781 4.06108C18.5237 4.31841 18.1959 4.49365 17.8389 4.56465C17.482 4.63564 17.112 4.59921 16.7758 4.45994C16.4396 4.32067 16.1522 4.08484 15.9501 3.78225C15.7479 3.47966 15.64 3.12392 15.64 2.76C15.64 2.272 15.8338 1.80399 16.1789 1.45892C16.524 1.11386 16.992 0.920001 17.48 0.920001Z"
        fill="#534588"
      />
      <path
        d="M4.60009 5.52C5.14596 5.52 5.67958 5.35813 6.13346 5.05486C6.58734 4.75159 6.9411 4.32053 7.15 3.81621C7.35889 3.31188 7.41355 2.75694 7.30706 2.22155C7.20056 1.68616 6.9377 1.19438 6.5517 0.808387C6.16571 0.422394 5.67392 0.159529 5.13854 0.0530339C4.60315 -0.0534613 4.04821 0.00119587 3.54388 0.210094C3.03956 0.418992 2.6085 0.772748 2.30523 1.22663C2.00196 1.68051 1.84009 2.21413 1.84009 2.76C1.84009 3.492 2.13087 4.19402 2.64847 4.71162C3.16607 5.22922 3.86809 5.52 4.60009 5.52ZM4.60009 0.920001C4.96401 0.920001 5.31975 1.02792 5.62234 1.2301C5.92492 1.43228 6.16076 1.71965 6.30003 2.05586C6.43929 2.39208 6.47573 2.76204 6.40473 3.11897C6.33374 3.47589 6.15849 3.80375 5.90117 4.06108C5.64384 4.31841 5.31598 4.49365 4.95905 4.56465C4.60213 4.63564 4.23217 4.59921 3.89595 4.45994C3.55973 4.32067 3.27237 4.08484 3.07018 3.78225C2.868 3.47966 2.76009 3.12392 2.76009 2.76C2.76009 2.272 2.95394 1.80399 3.29901 1.45892C3.64408 1.11386 4.11209 0.920001 4.60009 0.920001Z"
        fill="#534588"
      />
      <path
        d="M17.48 6.44014C16.7637 6.44104 16.0576 6.60998 15.4185 6.93339C14.7794 7.25679 14.225 7.72563 13.8 8.30222C13.009 7.69254 12.0384 7.36205 11.0397 7.36238C10.041 7.36271 9.0706 7.69385 8.28002 8.30406C7.70619 7.52527 6.90084 6.94783 5.97909 6.65429C5.05734 6.36074 4.06639 6.36611 3.14787 6.66962C2.22936 6.97314 1.43031 7.55927 0.864951 8.34423C0.299589 9.12918 -0.00314452 10.0728 2.46303e-05 11.0401V13.3401C2.46303e-05 13.7061 0.145417 14.0571 0.404217 14.3159C0.663017 14.5747 1.01403 14.7201 1.38002 14.7201H1.84002V18.8601C1.84002 19.2261 1.98542 19.5771 2.24422 19.8359C2.50302 20.0947 2.85403 20.2401 3.22002 20.2401H5.98002C6.34602 20.2401 6.69703 20.0947 6.95583 19.8359C7.21463 19.5771 7.36002 19.2261 7.36002 18.8601V16.4755C7.50735 16.53 7.66296 16.5586 7.82002 16.5601H8.28002V21.6201C8.28002 21.9861 8.42542 22.3371 8.68422 22.5959C8.94302 22.8547 9.29403 23.0001 9.66002 23.0001H12.42C12.786 23.0001 13.137 22.8547 13.3958 22.5959C13.6546 22.3371 13.8 21.9861 13.8 21.6201V16.5601H14.26C14.4171 16.5586 14.5727 16.53 14.72 16.4755V18.8601C14.72 19.2261 14.8654 19.5771 15.1242 19.8359C15.383 20.0947 15.734 20.2401 16.1 20.2401H18.86C19.226 20.2401 19.577 20.0947 19.8358 19.8359C20.0946 19.5771 20.24 19.2261 20.24 18.8601V14.7201H20.7C21.066 14.7201 21.417 14.5747 21.6758 14.3159C21.9346 14.0571 22.08 13.7061 22.08 13.3401V11.0401C22.08 10.4361 21.961 9.83789 21.7299 9.27979C21.4987 8.7217 21.1599 8.21459 20.7327 7.78744C20.3056 7.3603 19.7985 7.02146 19.2404 6.79029C18.6823 6.55912 18.0841 6.44014 17.48 6.44014ZM6.44002 18.8601C6.44002 18.9821 6.39156 19.0991 6.30529 19.1854C6.21903 19.2717 6.10202 19.3201 5.98002 19.3201H3.22002C3.09803 19.3201 2.98102 19.2717 2.89476 19.1854C2.80849 19.0991 2.76002 18.9821 2.76002 18.8601V14.7201C2.76002 14.4761 2.6631 14.2421 2.49056 14.0696C2.31803 13.8971 2.08402 13.8001 1.84002 13.8001H1.38002C1.25803 13.8001 1.14102 13.7517 1.05476 13.6654C0.968489 13.5791 0.920025 13.4621 0.920025 13.3401V11.0401C0.917521 10.2576 1.16498 9.49468 1.62635 8.86259C2.08771 8.2305 2.73887 7.76226 3.48494 7.5261C4.23101 7.28993 5.03301 7.29819 5.77406 7.54966C6.51511 7.80113 7.15649 8.28268 7.60474 8.92414C6.85687 9.75878 6.44228 10.8394 6.44002 11.9601V18.8601ZM14.26 15.6401H13.8C13.556 15.6401 13.322 15.7371 13.1495 15.9096C12.977 16.0821 12.88 16.3161 12.88 16.5601V21.6201C12.88 21.7421 12.8316 21.8591 12.7453 21.9454C12.659 22.0317 12.542 22.0801 12.42 22.0801H9.66002C9.53802 22.0801 9.42102 22.0317 9.33475 21.9454C9.24849 21.8591 9.20002 21.7421 9.20002 21.6201V16.5601C9.20002 16.3161 9.1031 16.0821 8.93056 15.9096C8.75803 15.7371 8.52402 15.6401 8.28002 15.6401H7.82002C7.69803 15.6401 7.58102 15.5917 7.49476 15.5054C7.40849 15.4191 7.36002 15.3021 7.36002 15.1801V11.9601C7.36002 10.9841 7.74774 10.0481 8.43787 9.35798C9.12801 8.66785 10.064 8.28014 11.04 8.28014C12.016 8.28014 12.952 8.66785 13.6422 9.35798C14.3323 10.0481 14.72 10.9841 14.72 11.9601V15.1801C14.72 15.3021 14.6716 15.4191 14.5853 15.5054C14.499 15.5917 14.382 15.6401 14.26 15.6401ZM21.16 13.3401C21.16 13.4621 21.1116 13.5791 21.0253 13.6654C20.939 13.7517 20.822 13.8001 20.7 13.8001H20.24C19.996 13.8001 19.762 13.8971 19.5895 14.0696C19.417 14.2421 19.32 14.4761 19.32 14.7201V18.8601C19.32 18.9821 19.2716 19.0991 19.1853 19.1854C19.099 19.2717 18.982 19.3201 18.86 19.3201H16.1C15.978 19.3201 15.861 19.2717 15.7748 19.1854C15.6885 19.0991 15.64 18.9821 15.64 18.8601V11.9601C15.6378 10.8394 15.2232 9.75878 14.4753 8.92414C14.9236 8.28268 15.5649 7.80113 16.306 7.54966C17.047 7.29819 17.849 7.28993 18.5951 7.5261C19.3412 7.76226 19.9923 8.2305 20.4537 8.86259C20.9151 9.49468 21.1625 10.2576 21.16 11.0401V13.3401Z"
        fill="#534588"
      />
    </svg>
  );
}

function MapLocationIcon() {
  return (
    <svg
      width="45"
      height="45"
      viewBox="0 0 45 45"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g filter="url(#filter0_d_1581_32182)">
        <path
          d="M34.7192 20.3628C34.7192 26.9902 29.3467 32.3628 22.7192 32.3628C16.0918 32.3628 10.7192 26.9902 10.7192 20.3628C10.7192 13.7354 16.0918 8.36279 22.7192 8.36279C29.3467 8.36279 34.7192 13.7354 34.7192 20.3628Z"
          fill="#5941B0"
        />
        <path
          d="M33.2192 20.3628C33.2192 26.1618 28.5182 30.8628 22.7192 30.8628C16.9202 30.8628 12.2192 26.1618 12.2192 20.3628C12.2192 14.5638 16.9202 9.86279 22.7192 9.86279C28.5182 9.86279 33.2192 14.5638 33.2192 20.3628Z"
          stroke="white"
          strokeOpacity="0.8"
          strokeWidth="3"
        />
      </g>
      <defs>
        <filter
          id="filter0_d_1581_32182"
          x="0.719238"
          y="0.362793"
          width="44"
          height="44"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset dy="2" />
          <feGaussianBlur stdDeviation="5" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix
            type="matrix"
            values="0 0 0 0 0.34902 0 0 0 0 0.254902 0 0 0 0 0.690196 0 0 0 0.75 0"
          />
          <feBlend
            mode="normal"
            in2="BackgroundImageFix"
            result="effect1_dropShadow_1581_32182"
          />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="effect1_dropShadow_1581_32182"
            result="shape"
          />
        </filter>
      </defs>
    </svg>
  );
}

function ListViewIcon() {
  return (
    <svg
      width="16"
      height="17"
      viewBox="0 0 16 17"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath="url(#clip0_799_30523)">
        <path
          d="M2.56 4.33984V15.5398C2.56027 15.7944 2.6615 16.0384 2.84148 16.2184C3.02145 16.3983 3.26548 16.4996 3.52 16.4998H12.48C12.7345 16.4996 12.9785 16.3983 13.1585 16.2184C13.3385 16.0384 13.4397 15.7944 13.44 15.5398V4.33984H2.56ZM5.44 12.6598C5.37671 12.6598 5.31484 12.6411 5.26222 12.6059C5.20959 12.5708 5.16858 12.5208 5.14436 12.4623C5.12014 12.4038 5.1138 12.3395 5.12615 12.2774C5.1385 12.2153 5.16897 12.1583 5.21373 12.1136C5.25848 12.0688 5.3155 12.0383 5.37757 12.026C5.43965 12.0136 5.50399 12.02 5.56246 12.0442C5.62093 12.0684 5.67091 12.1094 5.70607 12.1621C5.74123 12.2147 5.76 12.2766 5.76 12.3398C5.76 12.4247 5.72629 12.5061 5.66628 12.5661C5.60626 12.6261 5.52487 12.6598 5.44 12.6598ZM5.44 10.0998C5.37671 10.0998 5.31484 10.0811 5.26222 10.0459C5.20959 10.0108 5.16858 9.96077 5.14436 9.9023C5.12014 9.84383 5.1138 9.77949 5.12615 9.71741C5.1385 9.65534 5.16897 9.59832 5.21373 9.55357C5.25848 9.50882 5.3155 9.47834 5.37757 9.46599C5.43965 9.45365 5.50399 9.45998 5.56246 9.4842C5.62093 9.50842 5.67091 9.54944 5.70607 9.60206C5.74123 9.65468 5.76 9.71655 5.76 9.77984C5.76 9.86471 5.72629 9.94611 5.66628 10.0061C5.60626 10.0661 5.52487 10.0998 5.44 10.0998ZM5.44 7.53984C5.37671 7.53984 5.31484 7.52108 5.26222 7.48591C5.20959 7.45075 5.16858 7.40077 5.14436 7.3423C5.12014 7.28383 5.1138 7.21949 5.12615 7.15741C5.1385 7.09534 5.16897 7.03832 5.21373 6.99357C5.25848 6.94882 5.3155 6.91834 5.37757 6.90599C5.43965 6.89365 5.50399 6.89998 5.56246 6.9242C5.62093 6.94842 5.67091 6.98944 5.70607 7.04206C5.74123 7.09468 5.76 7.15655 5.76 7.21984C5.76 7.30471 5.72629 7.38611 5.66628 7.44612C5.60626 7.50613 5.52487 7.53984 5.44 7.53984ZM10.56 12.6598H6.72C6.63513 12.6598 6.55374 12.6261 6.49373 12.5661C6.43372 12.5061 6.4 12.4247 6.4 12.3398C6.4 12.255 6.43372 12.1736 6.49373 12.1136C6.55374 12.0536 6.63513 12.0198 6.72 12.0198H10.56C10.6449 12.0198 10.7263 12.0536 10.7863 12.1136C10.8463 12.1736 10.88 12.255 10.88 12.3398C10.88 12.4247 10.8463 12.5061 10.7863 12.5661C10.7263 12.6261 10.6449 12.6598 10.56 12.6598ZM10.56 10.0998H6.72C6.63513 10.0998 6.55374 10.0661 6.49373 10.0061C6.43372 9.94611 6.4 9.86471 6.4 9.77984C6.4 9.69497 6.43372 9.61358 6.49373 9.55357C6.55374 9.49356 6.63513 9.45984 6.72 9.45984H10.56C10.6449 9.45984 10.7263 9.49356 10.7863 9.55357C10.8463 9.61358 10.88 9.69497 10.88 9.77984C10.88 9.86471 10.8463 9.94611 10.7863 10.0061C10.7263 10.0661 10.6449 10.0998 10.56 10.0998ZM10.56 7.53984H6.72C6.63513 7.53984 6.55374 7.50613 6.49373 7.44612C6.43372 7.38611 6.4 7.30471 6.4 7.21984C6.4 7.13497 6.43372 7.05358 6.49373 6.99357C6.55374 6.93356 6.63513 6.89984 6.72 6.89984H10.56C10.6449 6.89984 10.7263 6.93356 10.7863 6.99357C10.8463 7.05358 10.88 7.13497 10.88 7.21984C10.88 7.30471 10.8463 7.38611 10.7863 7.44612C10.7263 7.50613 10.6449 7.53984 10.56 7.53984Z"
          fill="white"
        />
        <path
          d="M12.48 1.78H11.52V0.82C11.52 0.735131 11.4863 0.653737 11.4263 0.593726C11.3663 0.533714 11.2849 0.5 11.2 0.5C11.1151 0.5 11.0337 0.533714 10.9737 0.593726C10.9137 0.653737 10.88 0.735131 10.88 0.82V1.78H8.32V0.82C8.32 0.735131 8.28629 0.653737 8.22628 0.593726C8.16626 0.533714 8.08487 0.5 8 0.5C7.91513 0.5 7.83374 0.533714 7.77373 0.593726C7.71372 0.653737 7.68 0.735131 7.68 0.82V1.78H5.12V0.82C5.12 0.735131 5.08629 0.653737 5.02628 0.593726C4.96626 0.533714 4.88487 0.5 4.8 0.5C4.71513 0.5 4.63374 0.533714 4.57373 0.593726C4.51372 0.653737 4.48 0.735131 4.48 0.82V1.78H3.52C3.26548 1.78027 3.02145 1.8815 2.84148 2.06148C2.6615 2.24145 2.56027 2.48548 2.56 2.74V3.7H13.44V2.74C13.4397 2.48548 13.3385 2.24145 13.1585 2.06148C12.9785 1.8815 12.7345 1.78027 12.48 1.78Z"
          fill="white"
        />
      </g>
      <defs>
        <clipPath id="clip0_799_30523">
          <rect
            width="16"
            height="16"
            fill="white"
            transform="translate(0 0.5)"
          />
        </clipPath>
      </defs>
    </svg>
  );
}

function MapViewIcon() {
  return (
    <svg
      width="16"
      height="17"
      viewBox="0 0 16 17"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath="url(#clip0_1578_31336)">
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M10.24 0.505859L6.40002 1.60346V15.8543L10.24 14.7567V0.505859Z"
          fill="white"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M5.76003 1.60962L2.48752 0.73704C2.34534 0.699103 2.19634 0.69436 2.05203 0.723178C1.90772 0.751996 1.77197 0.813604 1.65527 0.903241C1.53856 0.992877 1.44403 1.10814 1.37896 1.24013C1.3139 1.37213 1.28005 1.51731 1.28003 1.66446V13.9283C1.28003 14.1398 1.34988 14.3454 1.47872 14.5131C1.60756 14.6808 1.78819 14.8013 1.99254 14.8558L5.76003 15.8602V1.60962Z"
          fill="white"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M14.6475 1.5048L10.88 0.5V14.7509L14.1525 15.6236C14.2947 15.6615 14.4437 15.6663 14.5881 15.6374C14.7324 15.6086 14.8681 15.547 14.9848 15.4573C15.1016 15.3677 15.1961 15.2524 15.2611 15.1204C15.3262 14.9883 15.36 14.8431 15.36 14.696V2.43222C15.36 2.22074 15.2901 2.01519 15.1613 1.84749C15.0324 1.67979 14.8518 1.55932 14.6475 1.5048Z"
          fill="white"
        />
      </g>
      <defs>
        <clipPath id="clip0_1578_31336">
          <rect
            width="16"
            height="16"
            fill="white"
            transform="translate(0 0.5)"
          />
        </clipPath>
      </defs>
    </svg>
  );
}

function FeatherChevronDown() {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M6 9L12 15L18 9"
        stroke="#06001E"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function FeatherChevronUp() {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M18 15L12 9L6 15"
        stroke="#06001E"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function TourClose() {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clip-path="url(#clip0_4441_5766)">
        <path
          d="M16.1793 7.82071C16.0893 7.73073 15.9672 7.68018 15.84 7.68018C15.7127 7.68018 15.5906 7.73073 15.5006 7.82071L12 11.3214L8.49933 7.82071C8.4088 7.73328 8.28755 7.6849 8.1617 7.68599C8.03584 7.68708 7.91545 7.73756 7.82646 7.82656C7.73746 7.91556 7.68698 8.03595 7.68589 8.1618C7.68479 8.28766 7.73317 8.4089 7.82061 8.49943L11.3213 12.0001L7.82061 15.5007C7.77477 15.545 7.7382 15.598 7.71304 15.6565C7.68789 15.7151 7.67464 15.7781 7.67409 15.8418C7.67354 15.9055 7.68568 15.9687 7.70982 16.0277C7.73395 16.0867 7.76959 16.1403 7.81466 16.1854C7.85973 16.2305 7.91332 16.2661 7.97231 16.2902C8.0313 16.3144 8.09451 16.3265 8.15824 16.326C8.22198 16.3254 8.28496 16.3122 8.34352 16.287C8.40209 16.2618 8.45505 16.2253 8.49933 16.1794L12 12.6788L15.5006 16.1794C15.5911 16.2669 15.7124 16.3153 15.8382 16.3142C15.9641 16.3131 16.0845 16.2626 16.1735 16.1736C16.2625 16.0846 16.313 15.9642 16.3141 15.8383C16.3151 15.7125 16.2668 15.5912 16.1793 15.5007L12.6787 12.0001L16.1793 8.49943C16.2693 8.40942 16.3199 8.28735 16.3199 8.16007C16.3199 8.03279 16.2693 7.91073 16.1793 7.82071Z"
          fill="#06001E"
        />
        <path
          d="M12 0C9.62663 0 7.30655 0.703788 5.33316 2.02236C3.35977 3.34094 1.8217 5.21509 0.913451 7.4078C0.00519939 9.60051 -0.232441 12.0133 0.230582 14.3411C0.693605 16.6689 1.83649 18.8071 3.51472 20.4853C5.19295 22.1635 7.33115 23.3064 9.65892 23.7694C11.9867 24.2324 14.3995 23.9948 16.5922 23.0866C18.7849 22.1783 20.6591 20.6402 21.9776 18.6668C23.2962 16.6935 24 14.3734 24 12C24 8.8174 22.7357 5.76516 20.4853 3.51472C18.2349 1.26428 15.1826 0 12 0V0ZM12 23.04C9.8165 23.04 7.68203 22.3925 5.86651 21.1794C4.05099 19.9663 2.63597 18.2421 1.80038 16.2248C0.964784 14.2075 0.746155 11.9878 1.17214 9.8462C1.59812 7.70465 2.64957 5.73751 4.19355 4.19354C5.73752 2.64957 7.70466 1.59811 9.84621 1.17213C11.9878 0.74615 14.2075 0.964779 16.2248 1.80037C18.2421 2.63596 19.9663 4.05099 21.1794 5.8665C22.3925 7.68202 23.04 9.81649 23.04 12C23.0367 14.927 21.8725 17.7331 19.8028 19.8028C17.7332 21.8725 14.927 23.0367 12 23.04Z"
          fill="#06001E"
        />
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M12 0C9.62663 0 7.30655 0.703788 5.33316 2.02236C3.35977 3.34094 1.8217 5.21509 0.913451 7.4078C0.00519939 9.60051 -0.232441 12.0133 0.230582 14.3411C0.693605 16.6689 1.83649 18.8071 3.51472 20.4853C5.19295 22.1635 7.33115 23.3064 9.65892 23.7694C11.9867 24.2324 14.3995 23.9948 16.5922 23.0866C18.7849 22.1783 20.6591 20.6402 21.9776 18.6668C23.2962 16.6935 24 14.3734 24 12C24 8.8174 22.7357 5.76516 20.4853 3.51472C18.2349 1.26428 15.1826 0 12 0V0ZM16.1794 15.5006C16.2668 15.5912 16.3152 15.7124 16.3141 15.8383C16.313 15.9641 16.2625 16.0845 16.1735 16.1735C16.0845 16.2625 15.9641 16.313 15.8383 16.3141C15.7124 16.3152 15.5912 16.2668 15.5006 16.1794L12 12.6787L8.49937 16.1794C8.40884 16.2668 8.28759 16.3152 8.16173 16.3141C8.03588 16.313 7.91549 16.2625 7.82649 16.1735C7.7375 16.0845 7.68702 15.9641 7.68592 15.8383C7.68483 15.7124 7.73321 15.5912 7.82065 15.5006L11.3213 12L7.82065 8.49936C7.7748 8.45508 7.73823 8.40212 7.71308 8.34355C7.68792 8.28499 7.67468 8.22201 7.67413 8.15827C7.67357 8.09454 7.68572 8.03133 7.70985 7.97234C7.73399 7.91335 7.76963 7.85976 7.8147 7.81469C7.85977 7.76962 7.91336 7.73398 7.97235 7.70985C8.03134 7.68571 8.09454 7.67357 8.15828 7.67412C8.22201 7.67467 8.285 7.68792 8.34356 7.71307C8.40212 7.73823 8.45509 7.7748 8.49937 7.82064L12 11.3213L15.5006 7.82064C15.5449 7.7748 15.5979 7.73823 15.6565 7.71307C15.715 7.68792 15.778 7.67467 15.8417 7.67412C15.9055 7.67357 15.9687 7.68571 16.0277 7.70985C16.0867 7.73398 16.1402 7.76962 16.1853 7.81469C16.2304 7.85976 16.266 7.91335 16.2902 7.97234C16.3143 8.03133 16.3264 8.09454 16.3259 8.15827C16.3253 8.22201 16.3121 8.28499 16.2869 8.34355C16.2618 8.40212 16.2252 8.45508 16.1794 8.49936L12.6787 12L16.1794 15.5006Z"
          fill={colors.brand.blackMinus90}
        />
      </g>
      <defs>
        <clipPath id="clip0_4441_5766">
          <rect width="24" height="24" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
}

function EyeOpen() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M8.00011 10.5C8.789 10.5 9.45845 10.225 10.0084 9.67496C10.5584 9.12496 10.8334 8.45551 10.8334 7.66663C10.8334 6.87774 10.5584 6.20829 10.0084 5.65829C9.45845 5.10829 8.789 4.83329 8.00011 4.83329C7.21123 4.83329 6.54178 5.10829 5.99178 5.65829C5.44178 6.20829 5.16678 6.87774 5.16678 7.66663C5.16678 8.45551 5.44178 9.12496 5.99178 9.67496C6.54178 10.225 7.21123 10.5 8.00011 10.5ZM8.00011 9.53329C7.47789 9.53329 7.03622 9.35274 6.67511 8.99163C6.314 8.63052 6.13345 8.18885 6.13345 7.66663C6.13345 7.1444 6.314 6.70274 6.67511 6.34163C7.03622 5.98051 7.47789 5.79996 8.00011 5.79996C8.52234 5.79996 8.964 5.98051 9.32511 6.34163C9.68622 6.70274 9.86678 7.1444 9.86678 7.66663C9.86678 8.18885 9.68622 8.63052 9.32511 8.99163C8.964 9.35274 8.52234 9.53329 8.00011 9.53329ZM8.00011 12.6666C6.46678 12.6666 5.06956 12.25 3.80845 11.4166C2.54734 10.5833 1.57234 9.48329 0.883447 8.11663C0.850114 8.06107 0.825114 7.9944 0.808447 7.91663C0.791781 7.83885 0.783447 7.75552 0.783447 7.66663C0.783447 7.57774 0.791781 7.4944 0.808447 7.41663C0.825114 7.33885 0.850114 7.27218 0.883447 7.21663C1.57234 5.84996 2.54734 4.74996 3.80845 3.91663C5.06956 3.08329 6.46678 2.66663 8.00011 2.66663C9.53345 2.66663 10.9307 3.08329 12.1918 3.91663C13.4529 4.74996 14.4279 5.84996 15.1168 7.21663C15.1501 7.27218 15.1751 7.33885 15.1918 7.41663C15.2084 7.4944 15.2168 7.57774 15.2168 7.66663C15.2168 7.75552 15.2084 7.83885 15.1918 7.91663C15.1751 7.9944 15.1501 8.06107 15.1168 8.11663C14.4279 9.48329 13.4529 10.5833 12.1918 11.4166C10.9307 12.25 9.53345 12.6666 8.00011 12.6666ZM8.00011 11.6666C9.34456 11.6666 10.5807 11.3027 11.7084 10.575C12.8362 9.84718 13.6946 8.87774 14.2834 7.66663C13.6946 6.45551 12.8362 5.48607 11.7084 4.75829C10.5807 4.03051 9.34456 3.66663 8.00011 3.66663C6.65567 3.66663 5.41956 4.03051 4.29178 4.75829C3.164 5.48607 2.30011 6.45551 1.70011 7.66663C2.30011 8.87774 3.164 9.84718 4.29178 10.575C5.41956 11.3027 6.65567 11.6666 8.00011 11.6666Z"
        fill="#514C61"
      />
    </svg>
  );
}

function EyeClosed() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M10.4833 9.01672L9.74999 8.28338C10.0389 7.49449 9.88888 6.83894 9.29999 6.31672C8.7111 5.79449 8.07221 5.66116 7.38333 5.91672L6.64999 5.18338C6.83888 5.06116 7.04999 4.97227 7.28333 4.91672C7.51666 4.86116 7.75555 4.83338 7.99999 4.83338C8.78888 4.83338 9.45833 5.10838 10.0083 5.65838C10.5583 6.20838 10.8333 6.87783 10.8333 7.66672C10.8333 7.91116 10.8028 8.15283 10.7417 8.39172C10.6805 8.6306 10.5944 8.83894 10.4833 9.01672ZM12.6333 11.1667L11.9667 10.5C12.5111 10.1 12.9861 9.65283 13.3917 9.15838C13.7972 8.66394 14.0944 8.16672 14.2833 7.66672C13.7278 6.43338 12.8944 5.45838 11.7833 4.74172C10.6722 4.02505 9.46666 3.66672 8.16666 3.66672C7.69999 3.66672 7.22221 3.71116 6.73333 3.80005C6.24444 3.88894 5.8611 3.99449 5.58333 4.11672L4.81666 3.33338C5.20555 3.1556 5.70277 3.00005 6.30832 2.86672C6.91388 2.73338 7.50555 2.66672 8.08333 2.66672C9.58333 2.66672 10.9667 3.07783 12.2333 3.90005C13.5 4.72227 14.4667 5.82783 15.1333 7.21672C15.1667 7.27227 15.1889 7.33894 15.2 7.41672C15.2111 7.49449 15.2167 7.57783 15.2167 7.66672C15.2167 7.7556 15.2111 7.84172 15.2 7.92505C15.1889 8.00838 15.1667 8.07783 15.1333 8.13338C14.8444 8.74449 14.4889 9.3056 14.0667 9.81672C13.6444 10.3278 13.1667 10.7778 12.6333 11.1667ZM13.2333 14.5667L10.8 12.1834C10.4111 12.3389 9.97221 12.4584 9.48333 12.5417C8.99444 12.625 8.49999 12.6667 7.99999 12.6667C6.46666 12.6667 5.0611 12.2556 3.78333 11.4334C2.50555 10.6112 1.53333 9.5056 0.866659 8.11672C0.833325 8.05005 0.811103 7.9806 0.799992 7.90838C0.788881 7.83616 0.783325 7.7556 0.783325 7.66672C0.783325 7.57783 0.791659 7.49172 0.808325 7.40838C0.824992 7.32505 0.844436 7.2556 0.866659 7.20005C1.09999 6.70005 1.39721 6.21394 1.75833 5.74172C2.11944 5.26949 2.54444 4.82227 3.03333 4.40005L1.28333 2.65005C1.18333 2.55005 1.13333 2.43338 1.13333 2.30005C1.13333 2.16672 1.18333 2.05005 1.28333 1.95005C1.38333 1.85005 1.50277 1.80005 1.64166 1.80005C1.78055 1.80005 1.89999 1.85005 1.99999 1.95005L13.9333 13.8834C14.0222 13.9723 14.0667 14.0806 14.0667 14.2084C14.0667 14.3362 14.0222 14.45 13.9333 14.55C13.8444 14.6612 13.7305 14.7167 13.5917 14.7167C13.4528 14.7167 13.3333 14.6667 13.2333 14.5667ZM3.71666 5.10005C3.30555 5.40005 2.90833 5.79449 2.52499 6.28338C2.14166 6.77227 1.86666 7.23338 1.69999 7.66672C2.26666 8.90005 3.11944 9.87505 4.25833 10.5917C5.39721 11.3084 6.68888 11.6667 8.13333 11.6667C8.49999 11.6667 8.8611 11.6445 9.21666 11.6C9.57221 11.5556 9.83888 11.4889 10.0167 11.4L8.94999 10.3334C8.82777 10.3889 8.67777 10.4306 8.49999 10.4584C8.32221 10.4862 8.15555 10.5 7.99999 10.5C7.22221 10.5 6.55555 10.2278 5.99999 9.68338C5.44444 9.13894 5.16666 8.46672 5.16666 7.66672C5.16666 7.50005 5.18055 7.33338 5.20833 7.16672C5.2361 7.00005 5.27777 6.85005 5.33333 6.71672L3.71666 5.10005Z"
        fill="#514C61"
      />
    </svg>
  );
}

function IconLocation() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <mask id="path-1-inside-1_193_21939" fill="white">
        <path d="M14.7708 0.640015L8.81627 16L7.17119 9.76627L0.639648 9.40056L14.7708 0.640015Z" />
      </mask>
      <path
        d="M14.7708 0.640015L15.7032 1.00147L16.7831 -1.78404L14.2439 -0.20991L14.7708 0.640015ZM8.81627 16L7.84937 16.2552L8.63532 19.2334L9.74866 16.3615L8.81627 16ZM0.639648 9.40056L0.112743 8.55064L-2.58276 10.2217L0.583744 10.399L0.639648 9.40056ZM7.17119 9.76627L8.13808 9.51111C8.02752 9.09215 7.65972 8.79206 7.22709 8.76784L7.17119 9.76627ZM13.8385 0.278558L7.88388 15.6386L9.74866 16.3615L15.7032 1.00147L13.8385 0.278558ZM1.16655 10.2505L15.2978 1.48994L14.2439 -0.20991L0.112743 8.55064L1.16655 10.2505ZM9.78317 15.7449L8.13808 9.51111L6.20429 10.0214L7.84937 16.2552L9.78317 15.7449ZM7.22709 8.76784L0.695553 8.40212L0.583744 10.399L7.11528 10.7647L7.22709 8.76784Z"
        fill="#5941B0"
        mask="url(#path-1-inside-1_193_21939)"
      />
    </svg>
  );
}

function IconLocationFilled() {
  return (
    <svg
      width="13"
      height="14"
      viewBox="0 0 13 14"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M13 0L7.52209 14L6.00869 8.3182L0 7.98487L13 0Z"
        fill="#5941B0"
      />
    </svg>
  );
}

function MenuIcon({ color }: { color?: string }) {
  return (
    <svg
      width="40"
      height="40"
      viewBox="0 0 40 40"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect
        x="8"
        y="24"
        width="24"
        height="1"
        rx="0.5"
        fill={color || "#06001E"}
      />
      <rect
        x="8"
        y="19"
        width="24"
        height="1"
        rx="0.5"
        fill={color || "#06001E"}
      />
      <rect
        x="8"
        y="14"
        width="24"
        height="1"
        rx="0.5"
        fill={color || "#06001E"}
      />
    </svg>
  );
}

function InviteMemberIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16">
      <path
        d="M6.11992 8.4C7.88723 8.4 9.31992 6.96731 9.31992 5.2C9.31992 3.43269 7.88723 2 6.11992 2C4.35261 2 2.91992 3.43269 2.91992 5.2C2.91992 6.96731 4.35261 8.4 6.11992 8.4Z"
        fill="white"
      />
      <path
        d="M6.12 9.04004C4.76209 9.04004 3.4598 9.57947 2.49961 10.5397C1.53943 11.4998 1 12.8021 1 14.16V14.48C1 14.5649 1.03371 14.6463 1.09373 14.7063C1.15374 14.7663 1.23513 14.8 1.32 14.8H10.92C11.0049 14.8 11.0863 14.7663 11.1463 14.7063C11.2063 14.6463 11.24 14.5649 11.24 14.48V14.16C11.24 12.8021 10.7006 11.4998 9.74039 10.5397C8.7802 9.57947 7.47791 9.04004 6.12 9.04004Z"
        fill="white"
      />
      <path
        d="M15.4002 5.83992H13.8002V4.23992C13.8002 4.15505 13.7665 4.07366 13.7065 4.01365C13.6465 3.95364 13.5651 3.91992 13.4802 3.91992C13.3954 3.91992 13.314 3.95364 13.254 4.01365C13.1939 4.07366 13.1602 4.15505 13.1602 4.23992V5.83992H11.5602C11.4754 5.83992 11.394 5.87364 11.334 5.93365C11.2739 5.99366 11.2402 6.07505 11.2402 6.15992C11.2402 6.24479 11.2739 6.32618 11.334 6.3862C11.394 6.44621 11.4754 6.47992 11.5602 6.47992H13.1602V8.07992C13.1602 8.16479 13.1939 8.24618 13.254 8.3062C13.314 8.36621 13.3954 8.39992 13.4802 8.39992C13.5651 8.39992 13.6465 8.36621 13.7065 8.3062C13.7665 8.24618 13.8002 8.16479 13.8002 8.07992V6.47992H15.4002C15.4851 6.47992 15.5665 6.44621 15.6265 6.3862C15.6865 6.32618 15.7202 6.24479 15.7202 6.15992C15.7202 6.07505 15.6865 5.99366 15.6265 5.93365C15.5665 5.87364 15.4851 5.83992 15.4002 5.83992Z"
        fill="white"
      />
    </svg>
  );
}

function IconPlusCircle() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M9.325 15H10.825V10.85H15V9.35H10.825V5H9.325V9.35H5V10.85H9.325V15ZM10 20C8.63333 20 7.34167 19.7375 6.125 19.2125C4.90833 18.6875 3.84583 17.9708 2.9375 17.0625C2.02917 16.1542 1.3125 15.0917 0.7875 13.875C0.2625 12.6583 0 11.3583 0 9.975C0 8.60833 0.2625 7.31667 0.7875 6.1C1.3125 4.88333 2.02917 3.825 2.9375 2.925C3.84583 2.025 4.90833 1.3125 6.125 0.7875C7.34167 0.2625 8.64167 0 10.025 0C11.3917 0 12.6833 0.2625 13.9 0.7875C15.1167 1.3125 16.175 2.025 17.075 2.925C17.975 3.825 18.6875 4.88333 19.2125 6.1C19.7375 7.31667 20 8.61667 20 10C20 11.3667 19.7375 12.6583 19.2125 13.875C18.6875 15.0917 17.975 16.1542 17.075 17.0625C16.175 17.9708 15.1167 18.6875 13.9 19.2125C12.6833 19.7375 11.3833 20 10 20ZM10.025 18.5C12.375 18.5 14.375 17.6708 16.025 16.0125C17.675 14.3542 18.5 12.3417 18.5 9.975C18.5 7.625 17.675 5.625 16.025 3.975C14.375 2.325 12.3667 1.5 10 1.5C7.65 1.5 5.64583 2.325 3.9875 3.975C2.32917 5.625 1.5 7.63333 1.5 10C1.5 12.35 2.32917 14.3542 3.9875 16.0125C5.64583 17.6708 7.65833 18.5 10.025 18.5Z"
        fill="#5941B0"
      />
    </svg>
  );
}

function CopyIcon() {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M11.25 17H7C5.58333 17 4.39583 16.5208 3.4375 15.5625C2.47917 14.6042 2 13.4167 2 12C2 10.5833 2.47917 9.39583 3.4375 8.4375C4.39583 7.47917 5.58333 7 7 7H11.25V8.5H7C6 8.5 5.16667 8.83333 4.5 9.5C3.83333 10.1667 3.5 11 3.5 12C3.5 13 3.83333 13.8333 4.5 14.5C5.16667 15.1667 6 15.5 7 15.5H11.25V17ZM8.125 12.75V11.25H15.875V12.75H8.125ZM12.75 17V15.5H17C18 15.5 18.8333 15.1667 19.5 14.5C20.1667 13.8333 20.5 13 20.5 12C20.5 11 20.1667 10.1667 19.5 9.5C18.8333 8.83333 18 8.5 17 8.5H12.75V7H17C18.4167 7 19.6042 7.47917 20.5625 8.4375C21.5208 9.39583 22 10.5833 22 12C22 13.4167 21.5208 14.6042 20.5625 15.5625C19.6042 16.5208 18.4167 17 17 17H12.75Z"
        fill="#5941B0"
      />
    </svg>
  );
}

function LaunchIcon({ color }: { color?: string }) {
  return (
    <svg
      width="16"
      height="17"
      viewBox="0 0 16 17"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M15.2675 1.23341C15.2379 1.20359 15.2026 1.17995 15.1638 1.16386C15.125 1.14776 15.0833 1.13954 15.0413 1.13965H9.92129C9.83642 1.13965 9.75503 1.17336 9.69502 1.23338C9.635 1.29339 9.60129 1.37478 9.60129 1.45965C9.60129 1.54452 9.635 1.62591 9.69502 1.68592C9.75503 1.74594 9.83642 1.77965 9.92129 1.77965H14.2688L6.49505 9.55341C6.43548 9.61352 6.40215 9.69478 6.40234 9.77941C6.40254 9.86403 6.43625 9.94513 6.4961 10.005C6.55595 10.0648 6.63706 10.0985 6.72169 10.0987C6.80631 10.0988 6.88756 10.0655 6.94766 10.0059L14.7213 2.23213V6.57965C14.7213 6.66452 14.755 6.74591 14.815 6.80592C14.875 6.86594 14.9564 6.89965 15.0413 6.89965C15.1262 6.89965 15.2076 6.86594 15.2676 6.80592C15.3276 6.74591 15.3613 6.66452 15.3613 6.57965V1.45965C15.3614 1.41762 15.3531 1.37598 15.337 1.33715C15.3209 1.29832 15.2973 1.26306 15.2675 1.23341Z"
        fill={color || "#5941B0"}
      />
      <path
        d="M13.7606 8.81992C13.6758 8.81992 13.5944 8.85364 13.5344 8.91365C13.4743 8.97366 13.4406 9.05505 13.4406 9.13992V14.8999C13.4406 14.9848 13.4069 15.0662 13.3469 15.1262C13.2869 15.1862 13.2055 15.2199 13.1206 15.2199H1.60062C1.51576 15.2199 1.43436 15.1862 1.37435 15.1262C1.31434 15.0662 1.28062 14.9848 1.28062 14.8999V3.37992C1.28062 3.29505 1.31434 3.21366 1.37435 3.15365C1.43436 3.09364 1.51576 3.05992 1.60062 3.05992H7.36062C7.44549 3.05992 7.52689 3.02621 7.5869 2.9662C7.64691 2.90618 7.68062 2.82479 7.68062 2.73992C7.68062 2.65505 7.64691 2.57366 7.5869 2.51365C7.52689 2.45364 7.44549 2.41992 7.36062 2.41992H1.60062C1.34602 2.41992 1.10184 2.52106 0.921802 2.7011C0.741768 2.88113 0.640625 3.12531 0.640625 3.37992L0.640625 14.8999C0.640625 15.1545 0.741768 15.3987 0.921802 15.5787C1.10184 15.7588 1.34602 15.8599 1.60062 15.8599H13.1206C13.3752 15.8599 13.6194 15.7588 13.7994 15.5787C13.9795 15.3987 14.0806 15.1545 14.0806 14.8999V9.13992C14.0806 9.05505 14.0469 8.97366 13.9869 8.91365C13.9269 8.85364 13.8455 8.81992 13.7606 8.81992Z"
        fill={color || "#5941B0"}
      />
    </svg>
  );
}

function MagnifyingGlass() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath="url(#clip0_1512_20423)">
        <path
          d="M15.0793 13.7213L11.9113 10.5533C11.7733 10.4113 11.5935 10.3171 11.3981 10.2846C11.2028 10.2521 11.0022 10.283 10.8256 10.3727L9.50185 9.04896C10.4455 7.98796 10.9386 6.60099 10.8765 5.18241C10.8143 3.76382 10.2018 2.42529 9.16896 1.45089C8.1361 0.476495 6.76418 -0.0570609 5.34439 -0.0365386C3.92459 -0.0160162 2.56867 0.556969 1.5644 1.56081C0.560134 2.56465 -0.0134267 3.92033 -0.0345514 5.34012C-0.0556761 6.75991 0.477298 8.13205 1.45126 9.16532C2.42521 10.1986 3.76349 10.8117 5.18204 10.8744C6.6006 10.9372 7.98779 10.4447 9.04918 9.50144L10.373 10.8252C10.2871 11.0026 10.2583 11.2023 10.2906 11.3967C10.3229 11.5912 10.4148 11.7708 10.5536 11.9108L13.7216 15.0788C13.9022 15.2557 14.1454 15.3542 14.3982 15.3528C14.651 15.3515 14.8931 15.2505 15.0719 15.0717C15.2507 14.8929 15.3518 14.6509 15.3531 14.398C15.3545 14.1452 15.2561 13.902 15.0792 13.7213H15.0793ZM5.44041 10.2401C4.49108 10.24 3.56307 9.95851 2.77374 9.43107C1.98441 8.90364 1.3692 8.15399 1.00591 7.27692C0.64262 6.39985 0.547565 5.43475 0.732763 4.50366C0.917961 3.57257 1.3751 2.7173 2.04636 2.04601C2.94817 1.15379 4.16652 0.654844 5.4351 0.658231C6.70369 0.661617 7.91935 1.16706 8.81639 2.06409C9.71342 2.96111 10.2189 4.17677 10.2223 5.44535C10.2257 6.71394 9.72674 7.93229 8.83452 8.83411C8.38984 9.28123 7.86089 9.63571 7.27829 9.87704C6.69569 10.1184 6.07101 10.2418 5.44041 10.2401ZM14.6267 14.6263C14.5666 14.6863 14.4853 14.72 14.4004 14.72C14.3156 14.72 14.2342 14.6863 14.1742 14.6263L11.0062 11.4583C10.9762 11.4286 10.9525 11.3933 10.9362 11.3545C10.92 11.3156 10.9116 11.2739 10.9115 11.2317C10.9114 11.1896 10.9196 11.1478 10.9357 11.1089C10.9518 11.0699 10.9754 11.0346 11.0052 11.0048C11.035 10.975 11.0704 10.9514 11.1094 10.9353C11.1483 10.9192 11.1901 10.911 11.2322 10.9111C11.2744 10.9112 11.3161 10.9196 11.355 10.9358C11.3938 10.9521 11.4291 10.9759 11.4588 11.0058L14.6268 14.1738C14.6868 14.2338 14.7205 14.3152 14.7205 14.4001C14.7205 14.4849 14.6868 14.5663 14.6268 14.6263H14.6267Z"
          fill="#514D61"
        />
      </g>
      <defs>
        <clipPath id="clip0_1512_20423">
          <rect width="16" height="16" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
}

function MailIcon() {
  return (
    <svg
      width="16"
      height="13"
      viewBox="0 0 16 13"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M15.04 0H0.96C0.705392 0 0.461212 0.101142 0.281177 0.281177C0.101143 0.461212 0 0.705392 0 0.96L0 11.2C0 11.4546 0.101143 11.6988 0.281177 11.8788C0.461212 12.0589 0.705392 12.16 0.96 12.16H15.04C15.2946 12.16 15.5388 12.0589 15.7188 11.8788C15.8989 11.6988 16 11.4546 16 11.2V0.96C16 0.705392 15.8989 0.461212 15.7188 0.281177C15.5388 0.101142 15.2946 0 15.04 0ZM0.64 0.96C0.64 0.875131 0.673714 0.793738 0.733726 0.733726C0.793737 0.673714 0.875131 0.64 0.96 0.64H15.04C15.1249 0.64 15.2063 0.673714 15.2663 0.733726C15.3263 0.793738 15.36 0.875131 15.36 0.96V2.1952L8.3264 6.3648C8.22768 6.42326 8.11505 6.4541 8.00032 6.4541C7.88559 6.4541 7.77296 6.42326 7.67424 6.3648L0.64 2.19648V0.96ZM15.36 11.2C15.36 11.2849 15.3263 11.3663 15.2663 11.4263C15.2063 11.4863 15.1249 11.52 15.04 11.52H0.96C0.875131 11.52 0.793737 11.4863 0.733726 11.4263C0.673714 11.3663 0.64 11.2849 0.64 11.2V2.94016L7.3472 6.9152C7.54472 7.03223 7.77009 7.09399 7.99968 7.09399C8.22927 7.09399 8.45464 7.03223 8.65216 6.9152L15.36 2.94016V11.2Z"
        fill="#5941B0"
      />
    </svg>
  );
}

function OutgoingIcon({ fill = colors.brand.eggplantcore }: { fill?: string }) {
  return (
    <svg
      fill={fill}
      width="16"
      height="15"
      viewBox="0 0 16 15"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M15.2656 0.234386C15.2359 0.20457 15.2007 0.180929 15.1619 0.164835C15.123 0.148741 15.0814 0.140512 15.0393 0.140626H9.91934C9.83447 0.140626 9.75307 0.17434 9.69306 0.234352C9.63305 0.294364 9.59934 0.375757 9.59934 0.460626C9.59934 0.545495 9.63305 0.626889 9.69306 0.6869C9.75307 0.746912 9.83447 0.780626 9.91934 0.780626H14.2669L6.4931 8.55439C6.43353 8.6145 6.40019 8.69576 6.40039 8.78038C6.40059 8.86501 6.4343 8.94611 6.49415 9.00594C6.554 9.06577 6.63511 9.09946 6.71974 9.09964C6.80436 9.09981 6.88561 9.06645 6.9457 9.00687L14.7193 1.23311V5.58063C14.7193 5.6655 14.7531 5.74689 14.8131 5.8069C14.8731 5.86691 14.9545 5.90063 15.0393 5.90063C15.1242 5.90063 15.2056 5.86691 15.2656 5.8069C15.3256 5.74689 15.3593 5.6655 15.3593 5.58063V0.460626C15.3594 0.418594 15.3512 0.376961 15.3351 0.338131C15.319 0.299301 15.2954 0.264041 15.2656 0.234386Z" />
      <path d="M13.7596 7.82187C13.6748 7.82187 13.5934 7.85559 13.5334 7.9156C13.4734 7.97561 13.4396 8.05701 13.4396 8.14187V13.9019C13.4396 13.9867 13.4059 14.0681 13.3459 14.1281C13.2859 14.1882 13.2045 14.2219 13.1196 14.2219H1.59965C1.51478 14.2219 1.43339 14.1882 1.37337 14.1281C1.31336 14.0681 1.27965 13.9867 1.27965 13.9019V2.38187C1.27965 2.29701 1.31336 2.21561 1.37337 2.1556C1.43339 2.09559 1.51478 2.06187 1.59965 2.06187H7.35965C7.44452 2.06187 7.52591 2.02816 7.58592 1.96815C7.64593 1.90814 7.67965 1.82674 7.67965 1.74187C7.67965 1.65701 7.64593 1.57561 7.58592 1.5156C7.52591 1.45559 7.44452 1.42187 7.35965 1.42188H1.59965C1.34504 1.42188 1.10086 1.52302 0.920826 1.70305C0.740791 1.88309 0.639648 2.12727 0.639648 2.38187L0.639648 13.9019C0.639648 14.1565 0.740791 14.4007 0.920826 14.5807C1.10086 14.7607 1.34504 14.8619 1.59965 14.8619H13.1196C13.3743 14.8619 13.6184 14.7607 13.7985 14.5807C13.9785 14.4007 14.0796 14.1565 14.0796 13.9019V8.14187C14.0796 8.05701 14.0459 7.97561 13.9859 7.9156C13.9259 7.85559 13.8445 7.82187 13.7596 7.82187Z" />
    </svg>
  );
}

function MapSearchedIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
    >
      <path
        d="M22.5 12C22.5 17.799 17.799 22.5 12 22.5C6.20101 22.5 1.5 17.799 1.5 12C1.5 6.20101 6.20101 1.5 12 1.5C17.799 1.5 22.5 6.20101 22.5 12Z"
        fill="#06001E"
        stroke="white"
        stroke-width="3"
      />
    </svg>
  );
}
